/**
 * Central editor state with Zustand.
 * Manages: loaded beatmap, playback, tool mode, selection, undo/redo.
 * Persists user preferences (camera, speed, volumes, etc.) to localStorage.
 */

import { create } from 'zustand';
import type {
  Beatmap, Difficulty, Note, Slide, Obstacle,
  ToolMode, SnapDivision, PlaybackState, CameraView,
  FlashEvent, BookmarkEntry,
} from '../types/beatmap';
import { GRID_SCALE } from '../types/beatmap';
import type { IntensityData } from '../heatmap/intensityEngine';
import type { PatternDifficultyReport } from '../api/patternDifficulty';
import { fetchPatternDifficulty } from '../api/patternDifficulty';
import defaultPrefs from '../config/defaultPrefs.json';
import type { DesignShape, DesignMesh, DesignPathNode, SavedShape } from '../types/designShape';
import { makeDesignShape, makeDefaultCustomPath, makeSavedShape, instantiateSavedShape, randomNeonColor, PRIMARY_MESH_ID } from '../types/designShape';
import type { KeyBinding, KeyCombo } from '../config/keybindings';
import { DEFAULT_KEYBINDINGS } from '../config/keybindings';
import { getApiBase } from '../api/apiBase';
import { listShapes, saveShape, deleteShape, setShapeVisibility } from '../api/shapes';


// Settings types + defaults now live in editorSettings.ts (extracted for
// modularity). Imported for local use here and re-exported so existing
// 'import { ... } from store/editorStore' calls keep working.
import {
  GridGuideType,
  ActiveView,
  FigureMode,
  RenderMode,
  MannequinSettings,
  ObstacleConfig,
  OBSTACLE_CONFIG_KEYS,
  ObstacleConfigKey,
  DEFAULT_OBSTACLE_CONFIGS,
  GameplaySettings,
  DEFAULT_MANNEQUIN,
  DEFAULT_GAMEPLAY,
  ParticleSettings,
  DEFAULT_PARTICLES,
  InterfaceSettings,
  DEFAULT_INTERFACE,
  migrateTypographyColors,
  PerfMode,
  PerformanceSettings,
  PerfPresetConfig,
  DEFAULT_PERFORMANCE,
} from './editorSettings';
// Synthesised editor SFX — reuse the Flow sound module (no duplication) so
// placing / deleting notes plays the same voices across every editor.
import { playPlaceSound, playDeleteSound } from '../editors/flow/flowSounds';
// Delete "explosion" particle burst + place "pop" stamps — rendered by TunnelScene.
import { emitNoteBurst, markPlaced, noteKey } from '../components/three/srBurst';
export * from './editorSettings';

/** Snap-grid subdivision family. `binary` = straight (…1/8, 1/4, 1/2),
 *  `ternary` = triplets (…1/6, 1/3, 2/3), `all` = both merged. Cycled with Tab. */
export type StepType = 'binary' | 'ternary' | 'all';
const BINARY_STEPS = [0.015625, 0.03125, 0.0625, 0.125, 0.25, 0.5, 1, 2, 4, 8];
// Triplet subdivisions of a beat: 1/24, 1/12, 1/6, 1/3, 2/3 (+ whole beats for coarse nav).
const TERNARY_STEPS = [1 / 24, 1 / 12, 1 / 6, 1 / 3, 2 / 3, 1, 2, 4];
export function stepsForType(type: StepType): number[] {
  if (type === 'ternary') return TERNARY_STEPS;
  if (type === 'all') return Array.from(new Set([...BINARY_STEPS, ...TERNARY_STEPS])).sort((a, b) => a - b);
  return BINARY_STEPS;
}

/** Performance mode: `auto` = the adaptive governor tunes draw distance by FPS;
 *  `performance`/`quality` force a short/long horizon (fewer/more objects);
 *  `potato` is the everything-off tier for weak machines (integrated/software
 *  GPUs). The type lives in editorSettings.ts next to the per-preset
 *  PerformanceSettings, whose DEFAULT_PERFORMANCE carries the values that used
 *  to be the PERF_MODE_BEATS / MODE_RENDER_SCALE / POTATO constants. */
export type { PerfMode } from './editorSettings';
function loadPerfMode(): PerfMode {
  try {
    const v = localStorage.getItem('synapse_perf_mode');
    if (v === 'auto' || v === 'performance' || v === 'quality' || v === 'potato') return v;
  } catch { /* ignore */ }
  return 'auto';
}

/** The rail's interpolated [x, y] at a given chart beat (uniform Catmull-Rom over
 *  the head + nodes), or null if the note has no rail or the beat is outside its
 *  span. rail_nodes[2] is encoded time (timeMs * 0.02). */
function _railXYAtBeat(note: Note, beat: number, bpm: number): [number, number] | null {
  if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;
  const dec = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
  const ctrl = [
    { b: note.time_beats, x: note.position[0], y: note.position[1] },
    ...note.rail_nodes.map(n => ({ b: dec(n[2]), x: n[0], y: n[1] })),
  ].sort((a, b) => a.b - b.b);
  const start = ctrl[0].b, end = ctrl[ctrl.length - 1].b;
  if (beat < start - 1e-6 || beat > end + 1e-6) return null;
  const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
    const t2 = t * t, t3 = t2 * t;
    return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
  };
  let i = 0;
  while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
  const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
  const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
  const seg = Math.max(1e-6, p2.b - p1.b);
  const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
  return [cr(p0.x, p1.x, p2.x, p3.x, t), cr(p0.y, p1.y, p2.y, p3.y, t)];
}

// ── LocalStorage Persistence ─────────────────────────────────────────────────

const STORAGE_KEY = 'abm-editor-prefs';

interface PersistedPrefs {
  cameraView?: CameraView;
  minimapCam?: { height: number; posZ: number; tilt: number };
  /** Minimap beat-spacing multiplier (0.5–2) — higher spreads packed notes. */
  minimapZoom?: number;
  /** Last scrub position (seconds) — restored on refresh so you return to where
   *  you were editing. Seconds (not beats) so it's bpm/offset-independent. */
  lastTime?: number;
  playbackSpeed?: number;
  scrollStep?: number;
  snapDivision?: SnapDivision;
  musicVolume?: number;
  sfxVolume?: number;
  activeDifficulty?: string;
  timelineZoom?: number;
  noteJumpSpeed?: number;
  mapLocked?: boolean;
  leftSidebarOpen?: boolean;
  leftSidebarWidth?: number;
  rightSidebarOpen?: boolean;
  rightSidebarWidth?: number;
  bottomPanelOpen?: boolean;
  bottomPanelHeight?: number;
  showHumanFigure?: boolean;
  figureMode?: FigureMode;
  renderMode?: RenderMode;
  heatmapEnabled?: boolean;
  patternDifficultyVisible?: boolean;
  fovPanelOpen?: boolean;
  customCameraPosition?: [number, number, number];
  customCameraRotation?: [number, number, number];
  defaultCameraPosition?: [number, number, number];
  defaultCameraRotation?: [number, number, number];
  leftCameraPosition?: [number, number, number];
  leftCameraRotation?: [number, number, number];
  rightCameraPosition?: [number, number, number];
  rightCameraRotation?: [number, number, number];
  mannequinSettings?: MannequinSettings;
  gameplaySettings?: GameplaySettings;
  interfaceSettings?: InterfaceSettings;
  particleSettings?: ParticleSettings;
  /** Per-preset performance knobs (Potato/Perf/Auto/Quality) — user-editable
   *  from the Performance settings tab; defaults in DEFAULT_PERFORMANCE. */
  performanceSettings?: PerformanceSettings;
  lastBeatmapId?: string;
  // Per-editor last-opened map (VR vs Console are independent).
  lastBeatmapIdByEditor?: Partial<Record<'synth-rider' | 'console', string>>;
  // Grid settings
  gridVisible?: boolean;
  gridSize?: number;
  snapToGrid?: boolean;
  gridGuideType?: string;
  gridGuideVisible?: boolean;
  gridWidth?: number;
  gridHeight?: number;
  gridDivisionsH?: number;
  gridDivisionsV?: number;
  gridColor?: string;
  gridOpacity?: number;
  notesGridYOffset?: number;
  activeView?: ActiveView;
  // Last-write-wins clock for cross-browser server sync (epoch ms). Set by
  // savePrefs; compared on boot to decide whether the server copy is newer.
  _savedAt?: number;
}

// Shipped defaults (camera, colors, gameplay, particles, mannequin, interface,
// grid, …) captured from a configured editor. They're the BASELINE: anything
// the user has saved in this browser's localStorage overrides them. On a fresh
// origin (e.g. the deployed site, with no localStorage and no backend) these
// become the visible defaults instead of the bare built-ins.
function loadPrefs(): PersistedPrefs {
  const base = defaultPrefs as unknown as PersistedPrefs;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...base, ...(JSON.parse(raw) as PersistedPrefs) };
  } catch { /* ignore parse errors */ }
  return { ...base };
}

// ── Server-side prefs sync ──────────────────────────────────────────────────
//
// We persist a copy of the localStorage prefs on the backend so they survive:
//   - the frontend running on a different port (each origin has its own LS)
//   - the user switching browsers / clearing site data
//   - first-time setup on a new machine that already runs the backend
//
// Strategy:
//   1. On boot, if localStorage is EMPTY, fetch from server and hydrate.
//      The fetch is async — we trigger a one-time reload after writing so
//      the synchronous store init picks up the new values.
//   2. On every savePrefs call, debounce and POST to /api/prefs.

const API_BASE = getApiBase();
const SERVER_SYNC_DEBOUNCE_MS = 1500;
let _serverSyncTimer: ReturnType<typeof setTimeout> | null = null;
let _lastSentJson = ''; // avoid pushing identical bodies repeatedly

function pushPrefsToServer(serialized: string) {
  if (_serverSyncTimer) clearTimeout(_serverSyncTimer);
  _serverSyncTimer = setTimeout(() => {
    if (serialized === _lastSentJson) return;
    fetch(`${API_BASE}/api/prefs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prefs: serialized }),
    }).then(res => {
      if (res.ok) _lastSentJson = serialized;
    }).catch(() => { /* offline / backend down — try again next save */ });
  }, SERVER_SYNC_DEBOUNCE_MS);
}

function savePrefs(prefs: PersistedPrefs) {
  let serialized = '';
  try {
    // Stamp a save clock so cross-browser sync can pick the most recent copy.
    serialized = JSON.stringify({ ...prefs, _savedAt: Date.now() });
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch { /* ignore quota errors */ }
  if (serialized) pushPrefsToServer(serialized);
}

// ── Flash-event draft (survives a browser refresh) ──────────────────────────
// SynthVR edits otherwise live only in memory: on refresh the map reloads from
// the backend (the original .synth, where a light has no strobe fields), so the
// frequency/repeat/duration would be lost. Mirror the per-map flash events to
// localStorage and restore them on load.
const FLASH_DRAFT_KEY = 'synapse_flash_drafts_v1';

function loadFlashDrafts(): Record<string, FlashEvent[]> {
  try { return JSON.parse(localStorage.getItem(FLASH_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadFlashDraft(id: string | null | undefined): FlashEvent[] | null {
  if (!id) return null;
  const all = loadFlashDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveFlashDraft(id: string | null | undefined, events: FlashEvent[]) {
  if (!id) return;
  try {
    const all = loadFlashDrafts();
    all[id] = events;
    localStorage.setItem(FLASH_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// Earliest beat across a copied notes+walls pair — the paste anchor. Kept as one
// helper so plain paste and mirror paste can never drift apart.
function _clipboardMinBeat(notes: Note[], slides: Slide[]): number {
  let min = Infinity;
  for (const n of notes) if (n.time_beats < min) min = n.time_beats;
  for (const s of slides) if (s.time_beats < min) min = s.time_beats;
  return Number.isFinite(min) ? min : 0;
}

// Side-locked wall types swap when mirrored; centre/crouch are symmetric.
const _MIRROR_WALL_TYPE: Record<string, string> = {
  wall_left: 'wall_right', wall_right: 'wall_left',
  angle_left: 'angle_right', angle_right: 'angle_left',
};

// Keybinding default migrations. Saved bindings normally win over defaults, but
// when a default changed *because the old one was the bug*, keeping the saved copy
// would ship the bug back to the people who reported it. Bump the version and list
// the affected ids together; only those are reset, all other customisations stay.
const KEYBINDINGS_VERSION_KEY = 'abm-keybindings-version';
// v3: A/D beat-step direction swapped per reviewer confirmation — D = finer
// subdivision, A = coarser (toward a whole step). Same ids as v2, so the reset
// list already covers them; only the version needs the bump.
const KEYBINDINGS_VERSION = 3;
const KEYBINDING_MIGRATED_IDS = new Set<string>([
  // v2: A/D now step the snap grid, and playback speed moved to Shift+Arrow so it
  // stops being shadowed by the identical beat-step arrow binding. paste_mirror
  // moved off Alt+V, which Windows browsers swallow into the menu bar.
  'beat_step_down', 'beat_step_up', 'playback_speed_down', 'playback_speed_up',
  'paste_mirror',
]);

// Design walls are a purely-decorative layer with no backend representation, so
// (like flash events) they live entirely in a per-map localStorage draft.
const DESIGN_DRAFT_KEY = 'synapse_decor_drafts_v1';
function loadDesignDrafts(): Record<string, DesignShape[]> {
  try { return JSON.parse(localStorage.getItem(DESIGN_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadDesignDraft(id: string | null | undefined): DesignShape[] | null {
  if (!id) return null;
  const all = loadDesignDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveDesignDraft(id: string | null | undefined, groups: DesignShape[]) {
  if (!id) return;
  try {
    const all = loadDesignDrafts();
    all[id] = groups;
    localStorage.setItem(DESIGN_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// ── Wall art LIBRARY (cross-project, like the old pattern library) ─────────────
// Saved wall arts the user liked — reusable in ANY project. A flat global list in
// localStorage holds only the PRE-FEATURE local library, read once as the
// migration source. The live library is server-backed and never written back
// to localStorage (a shared browser must not cache one user's shapes).
const DESIGN_LIB_KEY = 'synapse_decor_library_v1';
function loadDesignLibrary(): SavedShape[] {
  try {
    const arr = JSON.parse(localStorage.getItem(DESIGN_LIB_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
// One-time migration flag: once the localStorage library has been uploaded to
// the server (as the user's private shapes), we never re-run the upload.
const DESIGN_LIB_MIGRATED_KEY = 'synapse_decor_migrated_v1';

// Bookmarks have the same problem as flash events: a refresh reloads the backend
// .synth (which may not have the new/renamed bookmarks), so mirror them to
// localStorage per map and restore on load.
const BOOKMARK_DRAFT_KEY = 'synapse_bookmark_drafts_v1';
function loadBookmarkDrafts(): Record<string, BookmarkEntry[]> {
  try { return JSON.parse(localStorage.getItem(BOOKMARK_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadBookmarkDraft(id: string | null | undefined): BookmarkEntry[] | null {
  if (!id) return null;
  const all = loadBookmarkDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveBookmarkDraft(id: string | null | undefined, bookmarks: BookmarkEntry[]) {
  if (!id) return;
  try {
    const all = loadBookmarkDrafts();
    all[id] = bookmarks;
    localStorage.setItem(BOOKMARK_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// Note edits (add/delete/move/paste, clearing a difficulty, undo/redo, …) live
// only in memory; a refresh reloads the backend .synth (the original), so the
// edits — including *deletions* — would be lost. Mirror the edited difficulties
// to a per-map localStorage draft and restore them on load, exactly like flash
// events / decor / bookmarks. Keyed by the (content-derived) beatmap id.
const DIFFICULTY_DRAFT_KEY = 'synapse_difficulty_drafts_v1';
function loadDifficultyDrafts(): Record<string, Record<string, Difficulty>> {
  try { return JSON.parse(localStorage.getItem(DIFFICULTY_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadDifficultyDraft(id: string | null | undefined): Record<string, Difficulty> | null {
  if (!id) return null;
  const all = loadDifficultyDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveDifficultyDraft(id: string | null | undefined, difficulties: Record<string, Difficulty>) {
  if (!id) return;
  try {
    const all = loadDifficultyDrafts();
    all[id] = difficulties;
    localStorage.setItem(DIFFICULTY_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

/**
 * Server prefs sync on boot — DELIBERATELY non-destructive.
 *
 * If THIS browser already has a local prefs copy, it is the source of truth and
 * we NEVER overwrite it from the server; we only back our copy up. This prevents
 * a concurrent context that shares the same backend (a second tab, another
 * browser left open, a dev preview on a different port) from clobbering your
 * settings on reload — which manifested as "my settings keep reverting".
 *
 * Only when localStorage is EMPTY (first run in this browser / new machine /
 * different port) do we hydrate from the server and reload once so the
 * synchronous store init picks the values up.
 */
function syncPrefsWithServer() {
  let localRaw: string | null = null;
  try { localRaw = localStorage.getItem(STORAGE_KEY); } catch { return; }

  // Have local prefs → we own them. Just push a backup; don't ever adopt.
  if (localRaw) {
    pushPrefsToServer(localRaw);
    return;
  }

  // Empty localStorage → first run here: hydrate from the server if it has a copy.
  fetch(`${API_BASE}/api/prefs`)
    .then(res => res.ok ? res.json() : null)
    .then(payload => {
      const serverStr = payload && typeof payload.prefs === 'string' ? payload.prefs as string : null;
      if (!serverStr) return;
      try {
        localStorage.setItem(STORAGE_KEY, serverStr);
        // eslint-disable-next-line no-console
        console.info('[prefs] Hydrated settings from backend (first run), reloading…');
        window.location.reload();
      } catch { /* quota — ignore */ }
    })
    .catch(() => { /* backend offline — keep local */ });
}

if (typeof window !== 'undefined') {
  syncPrefsWithServer();
}

const savedPrefs = loadPrefs();

// ── Sanitize persisted gameplaySettings (fill missing fields in nested obstacleConfigs) ──

function sanitizeGameplaySettings(saved?: Partial<GameplaySettings>): GameplaySettings {
  const merged = { ...DEFAULT_GAMEPLAY, ...saved };
  // Deep-merge each obstacle config so new fields (e.g. scaleZ) get defaults
  if (saved?.obstacleConfigs) {
    const sanitized: Record<ObstacleConfigKey, ObstacleConfig> = { ...DEFAULT_OBSTACLE_CONFIGS };
    for (const key of OBSTACLE_CONFIG_KEYS) {
      sanitized[key] = { ...DEFAULT_OBSTACLE_CONFIGS[key], ...saved.obstacleConfigs[key] };
    }
    merged.obstacleConfigs = sanitized;
  }
  return merged;
}

/** Deep-merge saved per-preset performance settings over the defaults, per
 *  preset AND per flowFx, so a blob from an older build gets new knobs filled
 *  in. Explicit nulls are meaningful ("adaptive") and survive; a WRONG-TYPED
 *  value falls back to the default — these numbers feed the render budget
 *  directly (setManualNoteView etc.), so garbage must not pass. */
function sanitizePerformanceSettings(saved?: Partial<PerformanceSettings>): PerformanceSettings {
  const num = (v: unknown, def: number | null): number | null =>
    v === null ? null : (typeof v === 'number' && Number.isFinite(v) ? v : def);
  const bool = (v: unknown, def: boolean): boolean => (typeof v === 'boolean' ? v : def);
  const out = {} as PerformanceSettings;
  for (const mode of Object.keys(DEFAULT_PERFORMANCE) as PerfMode[]) {
    const def = DEFAULT_PERFORMANCE[mode];
    const s: Partial<PerfPresetConfig> = (saved?.[mode] && typeof saved[mode] === 'object') ? saved[mode]! : {};
    const fx = (s.flowFx && typeof s.flowFx === 'object') ? s.flowFx : ({} as Partial<typeof def.flowFx>);
    out[mode] = {
      drawDistanceBeats: num(s.drawDistanceBeats, def.drawDistanceBeats),
      renderScale: num(s.renderScale, def.renderScale),
      ladderSeed: num(s.ladderSeed, def.ladderSeed),
      fpsCap: num(s.fpsCap, def.fpsCap) ?? def.fpsCap,
      dprCap: num(s.dprCap, def.dprCap),
      antialias: bool(s.antialias, def.antialias),
      flowFx: {
        sparks: bool(fx.sparks, def.flowFx.sparks),
        waveformSprite: bool(fx.waveformSprite, def.flowFx.waveformSprite),
        railSweep: bool(fx.railSweep, def.flowFx.railSweep),
        beatChipsAllBeats: bool(fx.beatChipsAllBeats, def.flowFx.beatChipsAllBeats),
        minimapLiveDuringPlay: bool(fx.minimapLiveDuringPlay, def.flowFx.minimapLiveDuringPlay),
      },
    };
  }
  return out;
}

// ── Undo/Redo ───────────────────────────────────────────────────────────────

interface HistoryEntry {
  label: string;
  difficulties: Record<string, Difficulty>;
}

// ── Store Shape ─────────────────────────────────────────────────────────────

// A per-editor snapshot of the loaded map, so VR and Console keep independent
// maps even though they share this store + the SynthRiderApp shell.
type EditorKey = 'synth-rider' | 'console';
interface MapSlot {
  beatmap: Beatmap | null;
  lastBeatmapId: string | null;
  activeDifficulty: string;
  bookmarks: BookmarkEntry[];
  flashEvents: FlashEvent[];
}

interface EditorState {
  beatmap: Beatmap | null;
  lastBeatmapId: string | null;
  activeDifficulty: string;
  // ── Shapes (decorative wall layer — see types/designShape.ts) ──
  designShapes: DesignShape[];
  selectedDesignShapeIds: string[];   // multi-select (box-select marks many; click marks one)
  // Which shape (primary or a part, by id — see PRIMARY_MESH_ID) is being
  // edited right now, so the 3D scene knows where to put the move-gizmo.
  activeDesignMeshId: string | null;
  // Which control node of a `custom` curve is selected (its drag-gizmo shows in
  // the scene); null = none. Reset whenever the selection/active shape changes.
  activeDesignNodeIndex: number | null;
  shapeDesignerOpen: boolean;
  designClipboard: DesignShape | null;
  designUndoStack: DesignShape[][];
  designRedoStack: DesignShape[][];
  // Cross-project saved wall arts ("My Library" tab) — persisted globally.
  designLibrary: SavedShape[];
  loadDesignLibraryFromServer: () => Promise<void>;
  addToDesignLibrary: (groupId: string) => void;
  removeFromDesignLibrary: (id: string) => void;
  setLibraryShapeVisibility: (id: string, isPublic: boolean) => Promise<void>;
  addLibraryArtToMap: (id: string) => void;
  addDesignShape: () => void;
  updateDesignShape: (id: string, patch: Partial<DesignShape>, force?: boolean) => void;
  updateDesignShapes: (ids: string[], patch: Partial<DesignShape>, force?: boolean) => void;
  updateDesignMesh: (groupId: string, partId: string, patch: Partial<DesignMesh>, force?: boolean) => void;
  // Custom-curve control nodes (edited via the scene gizmo + the panel node list).
  updateDesignNode: (groupId: string, index: number, patch: Partial<DesignPathNode>, force?: boolean) => void;
  addDesignNode: (groupId: string) => void;
  removeDesignNode: (groupId: string, index: number) => void;
  setActiveDesignNodeIndex: (index: number | null) => void;
  removeDesignShape: (id: string) => void;
  removeDesignShapes: (ids: string[]) => void;
  duplicateDesignShape: (id: string, name?: string) => void;
  selectDesignShape: (id: string | null) => void;
  setSelectedDesignShapeIds: (ids: string[]) => void;
  setActiveDesignMeshId: (id: string | null) => void;
  setShapeDesignerOpen: (open: boolean) => void;
  copyDesignShape: () => void;
  pasteDesignShape: () => void;
  undoDesign: () => void;
  redoDesign: () => void;
  // Per-editor map isolation. `mapOwner` = which editor the LIVE beatmap belongs
  // to; `mapSlots` stashes each editor's map when you switch away; the per-editor
  // last id lets a refresh restore the right map for each editor.
  mapOwner: EditorKey | null;
  mapSlots: Partial<Record<EditorKey, MapSlot>>;
  lastBeatmapIdByEditor: Partial<Record<EditorKey, string>>;
  playback: PlaybackState;
  /** Scrub position (seconds) to restore once the audio loads (refresh). */
  lastEditTime: number;
  toolMode: ToolMode;
  snapDivision: SnapDivision;
  selectedNoteIds: number[];
  /** Selected obstacle (slide) indices — parallel to selectedNoteIds so notes
   *  AND regular obstacles can be box-/select-all'd and deleted together. */
  selectedSlideIds: number[];
  hoveredBeat: number;
  scrollStep: number;
  stepType: StepType;
  /** Mirror Mode: when ON, every note/wall you place also drops its mirror
   *  (opposite hand, X-mirrored). `mirrorInvertY` additionally flips it in Y. */
  mirrorMode: boolean;
  mirrorInvertY: boolean;
  /** Same-Note-Overwrite: when ON, placing a note where one already sits (same
   *  cell + beat) replaces it instead of stacking a duplicate. */
  sameNoteOverwrite: boolean;
  playbackSpeed: number;
  musicVolume: number;
  sfxVolume: number;
  cameraView: CameraView;
  timelineZoom: number;
  noteJumpSpeed: number;
  mapLocked: boolean;
  loopEnabled: boolean;
  metronomeEnabled: boolean;
  waveformVisible: boolean;
  showHumanFigure: boolean;
  figureMode: FigureMode;
  renderMode: RenderMode;
  heatmapEnabled: boolean;
  intensityData: IntensityData | null;
  // Pattern Difficulty (hand-aware, computed in backend) ────────────────
  /** Hand-aware difficulty report for the active difficulty. */
  patternDifficulty: PatternDifficultyReport | null;
  /** True while a fetch is in flight (avoid duplicate requests). */
  patternDifficultyLoading: boolean;
  /** Player arm span in meters, measured from the loaded mannequin FBX. */
  armSpanMeters: number | null;
  customCameraPosition: [number, number, number] | null;
  customCameraRotation: [number, number, number] | null;
  // Preset camera overrides (null = use built-in defaults)
  defaultCameraPosition: [number, number, number] | null;
  defaultCameraRotation: [number, number, number] | null;
  leftCameraPosition: [number, number, number] | null;
  leftCameraRotation: [number, number, number] | null;
  rightCameraPosition: [number, number, number] | null;
  rightCameraRotation: [number, number, number] | null;
  isFlyModeActive: boolean;
  saveCustomCameraRequested: boolean;
  settingsOpen: boolean;
  /** The performance benchmark overlay (own canvas + synthetic workload). While
   *  open, both editors' canvases stop rendering so the measurement is clean. */
  benchmarkOpen: boolean;
  adminUsersOpen: boolean;
  mannequinSettings: MannequinSettings;
  gameplaySettings: GameplaySettings;
  interfaceSettings: InterfaceSettings;
  particleSettings: ParticleSettings;
  fileManagerOpen: boolean;
  editInfoOpen: boolean;
  newMapOpen: boolean;
  leftSidebarOpen: boolean;
  leftSidebarWidth: number;
  rightSidebarOpen: boolean;
  rightSidebarWidth: number;
  bottomPanelOpen: boolean;
  bottomPanelHeight: number;
  // Focus mode — slide ALL chrome (both sidebars + the playback bar) off-screen
  // with an ease animation for a clean, unobstructed 3D view. Session-only (not
  // persisted, so a refresh always comes back with the panels visible).
  uiHidden: boolean;
  // "Project saved" toast — rendered by App above the playback bar (anchored to
  // its top-left) so it aligns with the playbar instead of floating centre-screen.
  projectSaved: boolean;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];

  // Pattern Difficulty meter visibility (top overlay above the 3D viewport)
  patternDifficultyVisible: boolean;
  // FOV / render panel visibility (floating popup opened from the transport).
  fovPanelOpen: boolean;
  /** Performance mode driving object density (see PerfMode). */
  perfMode: PerfMode;

  // Top-level navigation between dashboard and the per-game editors.
  activeView: ActiveView;

  // ── Keybinding System ──────────────────────────────────────────
  keybindings: KeyBinding[];
  keybindingsOpen: boolean;

  // ── Clipboard ──────────────────────────────────────────────────
  clipboard: Note[];
  /** Walls/obstacles copied alongside the notes — marquee-select picks them up and
   *  Delete removes them, so copy/paste has to carry them too. */
  clipboardSlides: Slide[];
  clipboardBpm: number;

  // ── Grid Settings ──────────────────────────────────────────────
  gridVisible: boolean;
  gridSize: number;
  snapToGrid: boolean;
  gridGuideType: GridGuideType;
  gridGuideVisible: boolean;
  gridWidth: number;      // total width of the trigger-area grid (world units)
  gridHeight: number;     // total height of the trigger-area grid (world units)
  gridDivisionsH: number; // horizontal (column) divisions
  gridDivisionsV: number; // vertical (row) divisions
  gridColor: string;
  gridOpacity: number;
  notesGridYOffset: number;  // Y offset for the notes placement grid

  // ── Rail Placement ─────────────────────────────────────────────
  railPlacementActive: boolean;
  railPlacementNoteIndex: number | null;

  // ── Rail Node Selection & Editing ─────────────────────────────
  selectedRailNode: { noteIndex: number; nodeIndex: number } | null;
  setSelectedRailNode: (noteIndex: number | null, nodeIndex: number | null) => void;
  updateRailNode: (noteIndex: number, nodeIndex: number, position: [number, number, number]) => void;
  removeRailNode: (noteIndex: number, nodeIndex: number) => void;

  // ── Note Preview (cursor ghost) ───────────────────────────────
  previewPosition: [number, number] | null;  // [x, y] in note-space, null when cursor not on grid
  setPreviewPosition: (pos: [number, number] | null) => void;

  // ── Note Hover / Gizmo ──────────────────────────────────────────
  hoveredNoteIndex: number | null;
  setHoveredNoteIndex: (index: number | null) => void;
  gizmoDragAxis: 'x' | 'y' | 'z' | null;  // which axis is being dragged
  setGizmoDragAxis: (axis: 'x' | 'y' | 'z' | null) => void;

  // ── Note Dragging ──────────────────────────────────────────────
  draggingNoteIndex: number | null;
  startDrag: (index: number) => void;
  finishDrag: (newBeat: number, newX: number, newY: number) => void;
  cancelDrag: () => void;

  // ── Bookmarks (named, global across difficulties) ───────────────
  bookmarks: BookmarkEntry[];
  addBookmark: (beat: number, name: string) => void;
  removeBookmark: (beat: number) => void;
  updateBookmarkName: (beat: number, name: string) => void;
  /** Move a bookmark from oldBeat to newBeat (drag the marker's arrow). */
  moveBookmark: (oldBeat: number, newBeat: number) => void;
  clearBookmarks: () => void;
  jumpToNextBookmark: () => void;
  jumpToPrevBookmark: () => void;
  bookmarkDialogOpen: boolean;
  setBookmarkDialogOpen: (open: boolean) => void;
  /** Beat of the bookmark open in the rename popup (click a marker / place one), or null. */
  editingBookmarkBeat: number | null;
  setEditingBookmarkBeat: (beat: number | null) => void;

  // ── Flash Events (global light effects) ────────────────────────
  flashEvents: FlashEvent[];
  addFlashEvent: (event: FlashEvent) => void;
  removeFlashEvent: (beat: number) => void;
  updateFlashEvent: (beat: number, updates: Partial<FlashEvent>) => void;
  /** Move a flash event from oldBeat to newBeat (drag the marker's arrow). */
  moveFlashEvent: (oldBeat: number, newBeat: number) => void;
  clearFlashEvents: () => void;
  flashDefaults: { color: string; intensity: number; fadeInTime: number; fadeOutTime: number; duration: number; repeat: number; frequency: number; };
  setFlashDefaults: (defaults: Partial<EditorState['flashDefaults']>) => void;
  /** Beat of the "primary" flash event whose values the editor popup shows, or null. */
  editingFlashBeat: number | null;
  setEditingFlashBeat: (beat: number | null) => void;
  /** Beats of all lights selected for editing — the popup applies edits to all. */
  selectedFlashBeats: number[];
  setSelectedFlashBeats: (beats: number[]) => void;
  /** Click a bulb: replace the selection (additive=false) or toggle it (Shift/Ctrl). */
  toggleFlashSelection: (beat: number, additive: boolean) => void;
  /** Apply the same updates to many lights at once. */
  updateFlashEvents: (beats: number[], updates: Partial<FlashEvent>) => void;

  loadBeatmap: (beatmap: Beatmap) => void;
  closeBeatmap: () => void;
  setActiveDifficulty: (name: string) => void;
  addDifficulty: (name: string) => void;
  updateMeta: (meta: Partial<Pick<Beatmap, 'name' | 'author' | 'beatmapper' | 'bpm' | 'offset'>>) => void;
  setPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  setCurrentBeat: (beat: number) => void;
  setDuration: (duration: number) => void;
  setToolMode: (mode: ToolMode, temporary?: boolean) => void;
  setSnapDivision: (snap: SnapDivision) => void;
  selectNote: (index: number, additive?: boolean) => void;
  selectNotes: (indices: number[]) => void;
  selectSlides: (indices: number[]) => void;
  clearSelection: () => void;
  /** Move the selected notes by whole grid cells in X / Y (keyboard nudge). */
  nudgeSelection: (dxCells: number, dyCells: number) => void;
  /** Right-drag loop region, in raw beats (same frame as note.time_beats). */
  loopRegion: { startBeat: number; endBeat: number } | null;
  setLoopRegion: (r: { startBeat: number; endBeat: number } | null) => void;
  setHoveredBeat: (beat: number) => void;
  setScrollStep: (step: number) => void;
  /** Cycle the snap-grid family binary → ternary → all, remapping the current
   *  step to the nearest value in the new family. */
  cycleStepType: () => void;
  setPlaybackSpeed: (speed: number) => void;
  setMusicVolume: (vol: number) => void;
  setSfxVolume: (vol: number) => void;
  setCameraView: (view: CameraView) => void;
  setTimelineZoom: (zoom: number) => void;
  setNoteJumpSpeed: (njs: number) => void;
  setMapLocked: (locked: boolean) => void;
  setLoopEnabled: (enabled: boolean) => void;
  setMetronomeEnabled: (enabled: boolean) => void;
  setWaveformVisible: (visible: boolean) => void;
  setShowHumanFigure: (show: boolean) => void;
  setFigureMode: (mode: FigureMode) => void;
  setRenderMode: (mode: RenderMode) => void;
  setHeatmapEnabled: (enabled: boolean) => void;
  setIntensityData: (data: IntensityData | null) => void;
  setArmSpanMeters: (m: number | null) => void;
  /** Async — fetches pattern difficulty for the active beatmap/difficulty. */
  refreshPatternDifficulty: () => Promise<void>;
  saveCustomCamera: (pos: [number, number, number], rot: [number, number, number]) => void;
  setCustomCameraPosition: (pos: [number, number, number]) => void;
  setCustomCameraRotation: (rot: [number, number, number]) => void;
  clearCustomCamera: () => void;
  setDefaultCameraPosition: (pos: [number, number, number]) => void;
  setDefaultCameraRotation: (rot: [number, number, number]) => void;
  setLeftCameraPosition: (pos: [number, number, number]) => void;
  setLeftCameraRotation: (rot: [number, number, number]) => void;
  setRightCameraPosition: (pos: [number, number, number]) => void;
  setRightCameraRotation: (rot: [number, number, number]) => void;
  resetCameraPreset: (preset: 'default' | 'left' | 'right') => void;
  setFlyModeActive: (active: boolean) => void;
  requestSaveCustomCamera: () => void;
  setSettingsOpen: (open: boolean) => void;
  setBenchmarkOpen: (open: boolean) => void;
  setAdminUsersOpen: (open: boolean) => void;
  setMannequinSettings: (s: Partial<MannequinSettings>) => void;
  setGameplaySettings: (s: Partial<GameplaySettings>) => void;
  setObstacleConfig: (key: ObstacleConfigKey, update: Partial<ObstacleConfig>) => void;
  resetMannequinSettings: () => void;
  resetGameplaySettings: () => void;
  setInterfaceSettings: (s: Partial<InterfaceSettings>) => void;
  resetInterfaceSettings: () => void;
  setParticleSettings: (s: Partial<ParticleSettings>) => void;
  resetParticleSettings: () => void;
  setFileManagerOpen: (open: boolean) => void;
  setEditInfoOpen: (open: boolean) => void;
  /** Minimap camera (height / forward-position / tilt) + the adjust panel open
   *  state. Mirrors the Flow editor's minimap-camera tuning. */
  minimapCam: { height: number; posZ: number; tilt: number };
  setMinimapCam: (patch: Partial<{ height: number; posZ: number; tilt: number }>) => void;
  minimapCamOpen: boolean;
  setMinimapCamOpen: (open: boolean) => void;
  /** Minimap beat spacing multiplier (0.5–2). Same control as Flow's minimap
   *  Zoom — turn it up and packed sections stop looking like a solid bar. */
  minimapZoom: number;
  setMinimapZoom: (v: number) => void;
  /** Camera View panel: are the pose controls (⚙) showing? */
  cameraOptionsOpen: boolean;
  setCameraOptionsOpen: (open: boolean) => void;
  setNewMapOpen: (open: boolean) => void;
  setLeftSidebarOpen: (open: boolean) => void;
  setLeftSidebarWidth: (w: number) => void;
  setRightSidebarOpen: (open: boolean) => void;
  setUiHidden: (hidden: boolean) => void;
  setProjectSaved: (v: boolean) => void;
  setRightSidebarWidth: (w: number) => void;
  setBottomPanelOpen: (open: boolean) => void;
  setBottomPanelHeight: (h: number) => void;
  // ── Keybinding Methods ───────────────────────────────────────
  setKeybinding: (id: string, slot: 'primary' | 'secondary', combo: KeyCombo) => void;
  resetKeybindings: () => void;
  setKeybindingsOpen: (open: boolean) => void;

  // ── Clipboard Methods ──────────────────────────────────────────
  copySelected: () => void;
  cutSelected: () => void;
  pasteAtBeat: (beat: number) => void;
  pasteMirrorAtBeat: (beat: number) => void;

  // ── Grid Methods ───────────────────────────────────────────────
  setGridVisible: (v: boolean) => void;
  setGridSize: (s: number) => void;
  setSnapToGrid: (v: boolean) => void;
  setGridGuideType: (t: GridGuideType) => void;
  setGridGuideVisible: (v: boolean) => void;
  setGridWidth: (w: number) => void;
  setGridHeight: (h: number) => void;
  setGridDivisionsH: (d: number) => void;
  setGridDivisionsV: (d: number) => void;
  setGridColor: (c: string) => void;
  setGridOpacity: (o: number) => void;
  setNotesGridYOffset: (v: number) => void;
  snapToGridPosition: (x: number, y: number) => [number, number];

  // ── Rail Placement Methods ─────────────────────────────────────
  startRailPlacement: (noteIndex: number) => void;
  addRailNode: (position: [number, number, number]) => void;
  finishRailPlacement: () => void;
  cancelRailPlacement: () => void;

  // ── Select All ─────────────────────────────────────────────────
  selectAll: () => void;

  addNote: (note: Note) => void;
  addNotes: (notes: Note[]) => void;
  replaceNotes: (notes: Note[]) => void;
  removeNotes: (indices: number[]) => void;
  updateNote: (index: number, update: Partial<Note>) => void;
  /** Batch position/time move for a set of notes by index, in one undo step —
   *  used when dragging a gizmo handle of a multi-selection (group move). */
  moveSelectedNotes: (updates: Array<{ index: number; position?: [number, number, number]; time?: { beats: number; ms: number } }>) => void;
  /** Take ONE undo snapshot at the start of an interactive drag, then suppress
   *  per-frame snapshots until endDrag() — undo returns to the pre-drag state. */
  beginDrag: (label: string) => void;
  endDrag: () => void;
  mirrorSelected: () => void;
  /** Flip selected notes over the Y axis (vertical): negate Y, keep hand. */
  flipSelectedY: () => void;
  /** Add a mirrored (opposite-hand, mirrored-X) copy of each selected note. */
  duplicateSelectedOppositeHand: () => void;
  /** Scale the TIME spacing of the selection around its earliest note by
   *  `factor` (>1 spreads apart, <1 compresses). */
  scaleSelection: (factor: number) => void;
  /** Wipe every object (notes, slides, crouches, jumps, shape obstacles) from
   *  the active difficulty. Undoable. */
  deleteAllObjects: () => void;
  /** Place a note, honoring Mirror Mode (drops the mirror in the same undo step). */
  addPlacedNote: (note: Note) => void;
  /** Place a wall, honoring Mirror Mode (mirrors wall_left↔right, angle_left↔right). */
  addPlacedSlide: (slide: Slide) => void;
  toggleMirrorMode: () => void;
  toggleMirrorInvertY: () => void;
  /** Join the selected notes (same hand) into one rail — earliest is the head,
   *  the rest become rail nodes. */
  joinNotesToRail: () => void;
  /** Split the rail at the selected rail node into two independent rails. */
  splitRailAtNode: () => void;
  /** Resample the selected rail's nodes onto the current snap step (Catmull-Rom). */
  recalcRailNodesByStep: () => void;
  /** Place a wall/obstacle of the given type at the playhead (legacy Ctrl+1..8). */
  placeWallAtPlayhead: (wallType: string) => void;
  /** Place a note of the active note-tool's hand at the playhead (centre). */
  placeNoteAtPlayhead: () => void;
  toggleSameNoteOverwrite: () => void;
  /** Snip: insert a node into the selected rail at the playhead beat (on-curve). */
  snipRailAtPlayhead: () => void;
  /** Cycle the selected notes' hand/type: right → left → one-hand → two-hand. */
  cycleSelectedNoteType: () => void;
  /** Snap each selected note onto the nearest rail's path at its own beat. */
  snapSelectionToRail: () => void;
  addSlide: (slide: Slide) => void;
  removeSlide: (index: number) => void;
  removeSlides: (indices: number[]) => void;
  updateSlide: (index: number, patch: Partial<Slide>) => void;
  sortSlides: () => void;
  addJump: (timeMs: number) => void;
  removeJump: (timeMs: number) => void;
  addCrouch: (timeMs: number) => void;
  removeCrouch: (timeMs: number) => void;
  moveCrouch: (oldMs: number, newMs: number) => void;
  moveJump: (oldMs: number, newMs: number) => void;
  toggleJumpAtBeat: (beat: number) => void;
  toggleCrouchAtBeat: (beat: number) => void;
  undo: () => void;
  redo: () => void;
  setPatternDifficultyVisible: (visible: boolean) => void;
  setFovPanelOpen: (open: boolean) => void;
  setPerfMode: (mode: PerfMode) => void;
  /** Per-preset performance knobs. Edited from the Performance settings tab in
   *  either editor — both read this one store, so changes apply everywhere. */
  performanceSettings: PerformanceSettings;
  setPerformancePreset: (preset: PerfMode, patch: Partial<PerfPresetConfig>) => void;
  resetPerformancePreset: (preset: PerfMode) => void;
  resetPerformanceSettings: () => void;
  setActiveView: (view: ActiveView) => void;
  /** Flush the in-memory edits to the per-map localStorage draft immediately
   *  (manual "Save" — the same drafts autosave writes, but on demand). */
  saveProjectDraft: () => void;
  clearCurrentDifficulty: () => void;
  clearAllDifficulties: () => void;
  getCurrentDifficulty: () => Difficulty | null;
  snapBeat: (beat: number) => number;
  beatToTimeMs: (beat: number) => number;
}

// ── Store ───────────────────────────────────────────────────────────────────

export const useEditorStore = create<EditorState>((set, get) => ({
  beatmap: null,
  lastBeatmapId: savedPrefs.lastBeatmapId || null,
  mapOwner: null,
  mapSlots: {},
  lastBeatmapIdByEditor: savedPrefs.lastBeatmapIdByEditor || {},
  activeDifficulty: savedPrefs.activeDifficulty || 'Expert',
  playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
  lastEditTime: savedPrefs.lastTime ?? 0,
  toolMode: 'select',
  snapDivision: savedPrefs.snapDivision || 16,
  selectedNoteIds: [],
  selectedSlideIds: [],
  hoveredBeat: 0,
  scrollStep: savedPrefs.scrollStep || 1,
  stepType: 'binary',
  mirrorMode: false,
  mirrorInvertY: false,
  sameNoteOverwrite: false,
  playbackSpeed: savedPrefs.playbackSpeed || 1,
  musicVolume: savedPrefs.musicVolume ?? 0.8,
  sfxVolume: savedPrefs.sfxVolume ?? 0.3,
  cameraView: (savedPrefs.cameraView || 'default') as CameraView,
  timelineZoom: savedPrefs.timelineZoom ?? 1,
  noteJumpSpeed: savedPrefs.noteJumpSpeed ?? 2,
  mapLocked: savedPrefs.mapLocked ?? false,
  loopEnabled: false,
  metronomeEnabled: false,
  waveformVisible: true,
  showHumanFigure: savedPrefs.showHumanFigure ?? false,
  figureMode: savedPrefs.figureMode ?? 'off',
  renderMode: savedPrefs.renderMode ?? 'auto',
  heatmapEnabled: savedPrefs.heatmapEnabled ?? false,
  intensityData: null,
  patternDifficulty: null,
  patternDifficultyLoading: false,
  armSpanMeters: null,
  customCameraPosition: savedPrefs.customCameraPosition || null,
  customCameraRotation: savedPrefs.customCameraRotation || null,
  defaultCameraPosition: savedPrefs.defaultCameraPosition || null,
  defaultCameraRotation: savedPrefs.defaultCameraRotation || null,
  leftCameraPosition: savedPrefs.leftCameraPosition || null,
  leftCameraRotation: savedPrefs.leftCameraRotation || null,
  rightCameraPosition: savedPrefs.rightCameraPosition || null,
  rightCameraRotation: savedPrefs.rightCameraRotation || null,
  isFlyModeActive: false,
  saveCustomCameraRequested: false,
  settingsOpen: false,
  benchmarkOpen: false,
  adminUsersOpen: false,
  mannequinSettings: { ...DEFAULT_MANNEQUIN, ...savedPrefs.mannequinSettings },
  gameplaySettings: sanitizeGameplaySettings(savedPrefs.gameplaySettings),
  performanceSettings: sanitizePerformanceSettings(savedPrefs.performanceSettings),
  interfaceSettings: migrateTypographyColors({ ...DEFAULT_INTERFACE, ...savedPrefs.interfaceSettings }),
  particleSettings: { ...DEFAULT_PARTICLES, ...savedPrefs.particleSettings },
  fileManagerOpen: false,
  editInfoOpen: false,
  newMapOpen: false,
  leftSidebarOpen: savedPrefs.leftSidebarOpen ?? true,
  leftSidebarWidth: savedPrefs.leftSidebarWidth ?? 200,
  rightSidebarOpen: savedPrefs.rightSidebarOpen ?? true,
  rightSidebarWidth: savedPrefs.rightSidebarWidth ?? 220,
  bottomPanelOpen: savedPrefs.bottomPanelOpen ?? true,
  bottomPanelHeight: savedPrefs.bottomPanelHeight ?? 300,
  uiHidden: false,
  projectSaved: false,
  undoStack: [],
  redoStack: [],
  patternDifficultyVisible: savedPrefs.patternDifficultyVisible ?? true,
  fovPanelOpen: savedPrefs.fovPanelOpen ?? false,
  perfMode: loadPerfMode(),
  activeView: (() => {
    const p = typeof window !== 'undefined' ? window.location.pathname : '';
    if (/^\/flow(\/|$)/.test(p)) return 'flow' as ActiveView;
    if (/^\/synth-vr(\/|$)/.test(p)) return 'synth-rider' as ActiveView;
    if (/^\/synth-console(\/|$)/.test(p)) return 'console' as ActiveView;
    return 'dashboard' as ActiveView;
  })(),

  // ── Keybinding System ──────────────────────────────────────────
  keybindings: (() => {
    // Reconcile any saved bindings against the CURRENT defaults rather than
    // trusting a saved copy blindly. A stale/partial `abm-keybindings` (older
    // schema, a dropped action, or a bad save) used to load verbatim — which
    // could silently leave a core action like toggle_play (Space → play/pause)
    // missing or unbound, killing that shortcut with no way to recover but a
    // manual reset. Now: start from the defaults (so every current action is
    // always present) and overlay only the user's saved primary/secondary for
    // ids that still exist.
    const defaults = DEFAULT_KEYBINDINGS;
    try {
      const raw = localStorage.getItem('abm-keybindings');
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length) {
          // Actions whose DEFAULT changed and whose old default was itself the
          // reported bug — a saved copy would keep shipping the broken binding to
          // exactly the people who complained. Adopt the new default for these and
          // keep every other personalisation. Bump the version + list together.
          const migratedIds = KEYBINDING_MIGRATED_IDS;
          const savedVersion = Number(localStorage.getItem(KEYBINDINGS_VERSION_KEY) || '0');
          const stale = savedVersion < KEYBINDINGS_VERSION;
          if (stale) {
            try { localStorage.setItem(KEYBINDINGS_VERSION_KEY, String(KEYBINDINGS_VERSION)); } catch { /* ignore */ }
          }
          const savedById = new Map<string, KeyBinding>(
            saved
              .filter((b: unknown): b is KeyBinding =>
                !!b && typeof (b as KeyBinding).id === 'string')
              .map((b: KeyBinding) => [b.id, b]),
          );
          return defaults.map((def) => {
            if (stale && migratedIds.has(def.id)) return def;
            const s = savedById.get(def.id);
            if (!s) return def;
            return {
              ...def,
              primary: s.primary ?? def.primary,
              secondary: s.secondary ?? def.secondary,
            };
          });
        }
      }
    } catch { /* ignore */ }
    try { localStorage.setItem(KEYBINDINGS_VERSION_KEY, String(KEYBINDINGS_VERSION)); } catch { /* ignore */ }
    return [...defaults];
  })(),
  keybindingsOpen: false,

  // ── Clipboard ──────────────────────────────────────────────────
  clipboard: [],
  clipboardSlides: [],
  clipboardBpm: 120,

  // ── Grid Settings ──────────────────────────────────────────────
  gridVisible: savedPrefs.gridVisible ?? true,
  gridSize: savedPrefs.gridSize ?? 0.1365,
  snapToGrid: savedPrefs.snapToGrid ?? true,
  gridGuideType: (savedPrefs.gridGuideType as GridGuideType) || 'solid',
  gridGuideVisible: savedPrefs.gridGuideVisible ?? true,
  gridWidth: savedPrefs.gridWidth ?? 3.6,
  gridHeight: savedPrefs.gridHeight ?? 2.6,
  gridDivisionsH: savedPrefs.gridDivisionsH ?? 8,
  gridDivisionsV: savedPrefs.gridDivisionsV ?? 6,
  gridColor: savedPrefs.gridColor ?? '#7C3AED',
  gridOpacity: savedPrefs.gridOpacity ?? 0.25,
  notesGridYOffset: savedPrefs.notesGridYOffset ?? 0,

  // ── Rail Placement ─────────────────────────────────────────────
  railPlacementActive: false,
  railPlacementNoteIndex: null,

  // ── Rail Node Selection & Editing ─────────────────────────────
  selectedRailNode: null,
  setSelectedRailNode: (noteIndex, nodeIndex) => {
    if (noteIndex === null || nodeIndex === null) {
      set({ selectedRailNode: null });
    } else {
      set({ selectedRailNode: { noteIndex, nodeIndex } });
    }
  },

  updateRailNode: (noteIndex, nodeIndex, position) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || nodeIndex >= note.rail_nodes.length) return;
    // Reject a time-drag that would land this node on ANOTHER node's exact
    // time (or the head's) — two nodes on one time position break the rail
    // (reviewer ask). node[2] is encoded time = timeMs * 0.02; ±2 ms epsilon.
    const EPS_ENC = 2 * 0.02;
    const clashes = note.rail_nodes.some((n2, i) => i !== nodeIndex && Math.abs(n2[2] - position[2]) < EPS_ENC)
      || Math.abs(note.time_ms * 0.02 - position[2]) < EPS_ENC;
    if (clashes) return;
    // Clamp X/Y to grid bounds (Z is time-based, leave as-is)
    const [cx, cy] = _clampNotePos(state, position[0], position[1]);
    const newNodes = [...note.rail_nodes];
    newNodes[nodeIndex] = [cx, cy, position[2]];
    get().updateNote(noteIndex, { rail_nodes: newNodes });
  },

  removeRailNode: (noteIndex, nodeIndex) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || nodeIndex >= note.rail_nodes.length) return;
    const newNodes = note.rail_nodes.filter((_: [number, number, number], i: number) => i !== nodeIndex);
    if (newNodes.length === 0) {
      // No more nodes — remove the rail entirely
      get().updateNote(noteIndex, { has_rail: false, rail_nodes: null });
      // If rail placement was active for this note, cancel it
      if (state.railPlacementActive && state.railPlacementNoteIndex === noteIndex) {
        set({ railPlacementActive: false, railPlacementNoteIndex: null });
      }
    } else {
      get().updateNote(noteIndex, { rail_nodes: newNodes });
    }
    // Clear selection if the removed node was selected
    if (state.selectedRailNode?.noteIndex === noteIndex && state.selectedRailNode?.nodeIndex === nodeIndex) {
      set({ selectedRailNode: null });
    }
  },

  // ── Note Preview / Hover / Gizmo ─────────────────────────────
  previewPosition: null,
  setPreviewPosition: (pos) => set({ previewPosition: pos }),
  hoveredNoteIndex: null,
  setHoveredNoteIndex: (index) => set({ hoveredNoteIndex: index }),
  gizmoDragAxis: null,
  setGizmoDragAxis: (axis) => set({ gizmoDragAxis: axis }),

  draggingNoteIndex: null,

  // Bookmarks (named, global)
  bookmarks: [],
  bookmarkDialogOpen: false,
  editingBookmarkBeat: null,

  // Shapes (decorative wall layer)
  designShapes: [],
  selectedDesignShapeIds: [],
  activeDesignMeshId: null,
  activeDesignNodeIndex: null,
  shapeDesignerOpen: false,
  designClipboard: null,
  designUndoStack: [],
  designRedoStack: [],
  designLibrary: loadDesignLibrary(),

  // Flash Events (global light effects)
  flashEvents: [],
  flashDefaults: {
    color: '#FBBF24',
    intensity: 1.0,
    fadeInTime: 0.05,
    fadeOutTime: 0.3,
    duration: 0.25,
    repeat: 1,
    frequency: 3,
  },
  editingFlashBeat: null,
  selectedFlashBeats: [],

  // ── Beatmap ───────────────────────────────────────────────────

  loadBeatmap: (beatmap) => {
    // Restore any locally-edited notes for this map; otherwise a refresh would
    // reload the backend's original .synth and silently undo the user's edits
    // (including deletions). The draft is the complete edited difficulties set.
    const diffDraft = loadDifficultyDraft(beatmap.id);
    if (diffDraft) beatmap = { ...beatmap, difficulties: diffDraft };
    const diffs = Object.keys(beatmap.difficulties);
    const saved = savedPrefs.activeDifficulty;
    const active = (saved && diffs.includes(saved)) ? saved
      : diffs.includes('Expert') ? 'Expert'
      : diffs.includes('Hard') ? 'Hard'
      : diffs[0] || 'Expert';
    // Restore any locally-edited flash events (strobe settings) for this map;
    // otherwise the backend's original lights (no strobe fields) would win.
    const flashDraft = loadFlashDraft(beatmap.id);
    const bookmarkDraft = loadBookmarkDraft(beatmap.id);
    const designDraft = loadDesignDraft(beatmap.id);
    // Attribute this map to the editor currently on screen so VR/Console stay
    // independent (and a refresh can restore each editor's own last map).
    const st = get();
    const owner: EditorKey = st.mapOwner
      ?? (st.activeView === 'console' ? 'console' : 'synth-rider');
    set({
      beatmap, lastBeatmapId: beatmap.id, activeDifficulty: active,
      mapOwner: owner,
      lastBeatmapIdByEditor: { ...st.lastBeatmapIdByEditor, [owner]: beatmap.id },
      selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
      bookmarks: bookmarkDraft ?? (beatmap.bookmarks || []),
      flashEvents: flashDraft ?? (beatmap.flashEvents || []),
      // Local edits (draft) win; otherwise restore decor embedded in the .synth
      // (present when the map was exported from Synapse), else none.
      designShapes: designDraft ?? (beatmap.decorWalls || []),
      selectedDesignShapeIds: [],
      activeDesignMeshId: null,
      activeDesignNodeIndex: null,
      designUndoStack: [], designRedoStack: [],
    });
  },

  closeBeatmap: () => {
    const st = get();
    // Also forget it for the owning editor so switching away + back (or a
    // refresh) doesn't resurrect the closed map.
    const slots = { ...st.mapSlots };
    const byEditor = { ...st.lastBeatmapIdByEditor };
    if (st.mapOwner) { delete slots[st.mapOwner]; delete byEditor[st.mapOwner]; }
    set({
      beatmap: null, lastBeatmapId: null, selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
      mapSlots: slots, lastBeatmapIdByEditor: byEditor,
      playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
    });
  },

  saveProjectDraft: () => {
    const st = get();
    const bm = st.beatmap;
    if (!bm) return;
    // Difficulty draft autosaves on a debounce; flush it now. Flash / decor /
    // bookmark drafts already persist on every change, so they're up to date.
    saveDifficultyDraft(bm.id, bm.difficulties);
    saveFlashDraft(bm.id, st.flashEvents);
    saveBookmarkDraft(bm.id, st.bookmarks);
    saveDesignDraft(bm.id, st.designShapes);
  },

  clearCurrentDifficulty: () => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Clear current difficulty');
    const diffName = state.activeDifficulty;
    const diff = state.beatmap.difficulties[diffName];
    if (!diff) return;
    const cleared: Difficulty = {
      ...diff,
      notes: [],
      slides: [],
      crouches: [],
      jumps: [],
      note_count: 0,
      rail_count: 0,
      slide_count: 0,
      crouch_count: 0,
      jump_count: 0,
    };
    set({
      beatmap: {
        ...state.beatmap,
        difficulties: { ...state.beatmap.difficulties, [diffName]: cleared },
      },
      selectedNoteIds: [],
    });
  },

  clearAllDifficulties: () => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Clear all difficulties');
    const clearedDiffs: Record<string, Difficulty> = {};
    for (const [name, diff] of Object.entries(state.beatmap.difficulties)) {
      clearedDiffs[name] = {
        ...diff,
        notes: [],
        slides: [],
        crouches: [],
        jumps: [],
        note_count: 0,
        rail_count: 0,
        slide_count: 0,
        crouch_count: 0,
        jump_count: 0,
      };
    }
    set({
      beatmap: { ...state.beatmap, difficulties: clearedDiffs },
      selectedNoteIds: [],
    });
  },

  setActiveDifficulty: (name) => set({ activeDifficulty: name, selectedNoteIds: [], selectedSlideIds: [] }),

  // Create an empty difficulty (default or custom-named) and switch to it.
  addDifficulty: (name) => {
    const state = get();
    if (!state.beatmap) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed in state.beatmap.difficulties) {
      if (trimmed) set({ activeDifficulty: trimmed, selectedNoteIds: [] });
      return;
    }
    pushUndo(state, `Add difficulty ${trimmed}`);
    const empty: Difficulty = {
      name: trimmed,
      note_count: 0, rail_count: 0, slide_count: 0, crouch_count: 0, jump_count: 0,
      duration_seconds: 0, duration_beats: 0,
      notes: [], slides: [], crouches: [], jumps: [],
    };
    set({
      beatmap: { ...state.beatmap, difficulties: { ...state.beatmap.difficulties, [trimmed]: empty } },
      activeDifficulty: trimmed,
      selectedNoteIds: [],
    });
  },

  updateMeta: (meta) => {
    const { beatmap } = get();
    if (!beatmap) return;
    set({ beatmap: { ...beatmap, ...meta } });
  },

  // ── Playback ──────────────────────────────────────────────────

  setPlaying: (playing) => {
    if (!playing) {
      // On pause: snap to the closest beat step line in the offset-adjusted CHART
      // beat frame (the frame the scene/placement use). beatmap.offset is in ms.
      // Uses the visible Step (scrollStep) so pausing lands on the same grid the
      // mapper is looking at — see snapBeat.
      const { scrollStep, beatmap, playback } = get();
      const bpm = beatmap?.bpm || 120;
      const offsetSec = (beatmap?.offset || 0) / 1000;
      const step = scrollStep > 0 ? scrollStep : 0.25;
      const chartBeat = ((playback.currentTime - offsetSec) / 60) * bpm;
      const snappedChartBeat = Math.max(0, Math.round(chartBeat / step) * step);
      // Audio time for that chart beat = chartBeat*60/bpm + offset.
      const snappedTime = (snappedChartBeat / bpm) * 60 + offsetSec;
      set({ playback: { ...playback, playing: false, currentTime: snappedTime, currentBeat: snappedChartBeat } });
    } else {
      set(s => ({ playback: { ...s.playback, playing: true } }));
    }
  },

  setCurrentTime: (time) => {
    const { beatmap, playback, scrollStep } = get();
    const bpm = beatmap?.bpm || 120;
    const offsetSec = (beatmap?.offset || 0) / 1000; // offset is stored in ms
    let currentBeat = Math.max(0, ((time - offsetSec) / 60) * bpm);
    let t = time;
    // While PAUSED the playhead always lands ON the step grid. The snap grid and
    // the bar lines are drawn at absolute beats, so a playhead sitting between
    // steps (after playback, a timeline click, a restored session, or any map
    // with an offset) made them look shifted off the trigger line — reviewers
    // read that as "the snap line isn't on the trigger" and "the bar line is a
    // step behind". Snapping here keeps the trigger ON the grid, which is what
    // "snap is measured from the trigger line" means. Playback stays exact.
    if (!playback.playing) {
      const step = scrollStep > 0 ? scrollStep : 0.25;
      currentBeat = Math.max(0, Math.round(currentBeat / step) * step);
      t = (currentBeat / bpm) * 60 + offsetSec;
    }
    set(s => ({ playback: { ...s.playback, currentTime: t, currentBeat } }));
  },

  setCurrentBeat: (beat) => {
    const { beatmap } = get();
    const bpm = beatmap?.bpm || 120;
    const offsetSec = (beatmap?.offset || 0) / 1000; // offset is stored in ms
    const clampedBeat = Math.max(0, beat);
    const time = (clampedBeat / bpm) * 60 + offsetSec;
    set(s => ({ playback: { ...s.playback, currentTime: time, currentBeat: clampedBeat } }));
  },

  setDuration: (duration) => set(s => ({ playback: { ...s.playback, duration } })),

  // ── Tools ─────────────────────────────────────────────────────

  setToolMode: (mode, temporary) => {
    const state = get();
    // If rail placement is active and this isn't a temporary switch (e.g. Ctrl-hold),
    // finish the current rail when switching to ANY different tool —
    // including switching between different rail tools (e.g. rail_right → rail_left)
    if (state.railPlacementActive && !temporary && mode !== state.toolMode) {
      get().finishRailPlacement();
    }

    // Auto-place/remove obstacle logic
    const OBSTACLE_WALL_MAP: Record<string, string> = {
      wall_left: 'wall_left', wall_right: 'wall_right',
      wall_diag_left: 'angle_left', wall_diag_right: 'angle_right',
      wall_center: 'center', crouch: 'crouch',
    };

    const prevWallType = OBSTACLE_WALL_MAP[state.toolMode];
    const newWallType = OBSTACLE_WALL_MAP[mode];

    // If toggling OFF an obstacle tool (clicking same tool again → select),
    // remove the obstacle nearest to the playhead that matches the wall type
    if (prevWallType && !newWallType && !temporary && state.beatmap) {
      const currentTime = state.playback.currentTime;
      const timeMs = currentTime * 1000;
      const diff = state.beatmap.difficulties?.[state.activeDifficulty];
      if (diff?.slides) {
        const threshold = 50; // 50ms tolerance
        const idx = diff.slides.findIndex(s =>
          s.wall_type === prevWallType && Math.abs(s.time_ms - timeMs) < threshold
        );
        if (idx >= 0) {
          get().removeSlide(idx);
        }
      }
    }

    set({ toolMode: mode });

    // Auto-place an obstacle at the playhead when an obstacle tool is selected
    // If an obstacle of the same type already exists at the playhead, remove it instead (toggle)
    if (newWallType && !temporary && state.beatmap) {
      const bpm = state.beatmap.bpm || 120;
      const currentTime = state.playback.currentTime;
      const rawBeat = (currentTime / 60) * bpm;
      const timeMs = currentTime * 1000;
      const timeSeconds = currentTime;
      const diff = state.beatmap.difficulties?.[state.activeDifficulty];
      const threshold = 50; // 50ms tolerance
      const existingIdx = diff?.slides?.findIndex(s =>
        s.wall_type === newWallType && Math.abs(s.time_ms - timeMs) < threshold
      ) ?? -1;

      if (existingIdx >= 0) {
        // Toggle off — remove the existing obstacle of this type at the playhead
        get().removeSlide(existingIdx);
      } else {
        // Place a new obstacle
        const slide: import('../types/beatmap').Slide = {
          time_ms: timeMs, time_seconds: timeSeconds, time_beats: rawBeat,
          slide_type: 0, wall_type: newWallType, initialized: true,
        };
        get().addSlide(slide);
      }
    }
  },
  setSnapDivision: (snap) => set({ snapDivision: snap }),

  // ── Selection (array-based) ───────────────────────────────────

  selectNote: (index, additive = false) => set(s => {
    if (additive) {
      const has = s.selectedNoteIds.includes(index);
      return { selectedNoteIds: has ? s.selectedNoteIds.filter(i => i !== index) : [...s.selectedNoteIds, index] };
    }
    // Non-additive click selects just this note — clear any obstacle selection
    // too so single-click selection is unambiguous.
    return { selectedNoteIds: [index], selectedSlideIds: [] };
  }),

  selectNotes: (indices) => set({ selectedNoteIds: indices }),
  selectSlides: (indices) => set({ selectedSlideIds: indices }),
  clearSelection: () => set({ selectedNoteIds: [], selectedSlideIds: [] }),
  loopRegion: null,
  setLoopRegion: (r) => set({ loopRegion: r }),

  nudgeSelection: (dxCells, dyCells) => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const SCALE = GRID_SCALE * 10;
    const stepX = (state.gridWidth / Math.max(1, state.gridDivisionsH)) / SCALE;
    const stepY = (state.gridHeight / Math.max(1, state.gridDivisionsV)) / SCALE;
    pushUndo(state, `Move ${state.selectedNoteIds.length} notes`);
    const sel = new Set(state.selectedNoteIds);
    // X/Y nudge only — time is unchanged, so no re-sort and indices stay valid.
    const newNotes = diff.notes.map((n: Note, i: number) => {
      if (!sel.has(i)) return n;
      const [cx, cy] = _clampNotePos(state, n.position[0] + dxCells * stepX, n.position[1] + dyCells * stepY);
      return { ...n, position: [cx, cy, n.position[2]] as [number, number, number] };
    });
    setDiffNotes(set, get, newNotes);
  },
  setHoveredBeat: (beat) => set({ hoveredBeat: beat }),
  setScrollStep: (step) => set({ scrollStep: step }),

  cycleStepType: () => {
    const { stepType, scrollStep } = get();
    const order: StepType[] = ['binary', 'ternary', 'all'];
    const next = order[(order.indexOf(stepType) + 1) % order.length];
    // Remap the current step to the nearest value in the new family so the grid
    // density stays roughly the same across the switch.
    const opts = stepsForType(next);
    let best = opts[0], bestDist = Infinity;
    for (const o of opts) {
      const d = Math.abs(o - scrollStep);
      if (d < bestDist) { bestDist = d; best = o; }
    }
    set({ stepType: next, scrollStep: best });
  },
  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
  setMusicVolume: (vol) => set({ musicVolume: Math.max(0, Math.min(1, vol)) }),
  setSfxVolume: (vol) => set({ sfxVolume: Math.max(0, Math.min(1, vol)) }),
  setCameraView: (view) => set({ cameraView: view }),
  setTimelineZoom: (zoom) => set({ timelineZoom: Math.max(0.25, Math.min(16, zoom)) }),
  setNoteJumpSpeed: (njs) => set({ noteJumpSpeed: Math.max(0.1, Math.min(20, njs)) }),
  setMapLocked: (locked) => set({ mapLocked: locked }),
  setLoopEnabled: (enabled) => set({ loopEnabled: enabled }),
  setMetronomeEnabled: (enabled) => set({ metronomeEnabled: enabled }),
  setWaveformVisible: (visible) => set({ waveformVisible: visible }),
  setShowHumanFigure: (show) => set({ showHumanFigure: show }),
  setFigureMode: (mode) => set({ figureMode: mode, showHumanFigure: mode !== 'off' }),
  setRenderMode: (mode) => set({ renderMode: mode }),
  setHeatmapEnabled: (enabled) => set({ heatmapEnabled: enabled }),
  setIntensityData: (data) => set({ intensityData: data }),
  setArmSpanMeters: (m) => {
    const prev = get().armSpanMeters;
    set({ armSpanMeters: m });
    // Re-fetch difficulty if arm span actually changed meaningfully (>1cm).
    if (m && (!prev || Math.abs(prev - m) > 0.01)) {
      get().refreshPatternDifficulty();
    }
  },
  refreshPatternDifficulty: async () => {
    const state = get();
    const beatmapId = state.beatmap?.id;
    const difficulty = state.activeDifficulty;
    if (!beatmapId || !difficulty) {
      set({ patternDifficulty: null });
      return;
    }
    if (state.patternDifficultyLoading) return;
    set({ patternDifficultyLoading: true });
    try {
      const report = await fetchPatternDifficulty({
        beatmapId,
        difficulty,
        armSpan: state.armSpanMeters ?? undefined,
      });
      // Guard against a newer fetch having superseded this one.
      const cur = get();
      if (cur.beatmap?.id === beatmapId && cur.activeDifficulty === difficulty) {
        set({ patternDifficulty: report, patternDifficultyLoading: false });
      } else {
        set({ patternDifficultyLoading: false });
      }
    } catch (err) {
      console.warn('[patternDifficulty] fetch failed:', err);
      set({ patternDifficulty: null, patternDifficultyLoading: false });
    }
  },
  saveCustomCamera: (pos, rot) => set({ customCameraPosition: pos, customCameraRotation: rot, saveCustomCameraRequested: false }),
  setCustomCameraPosition: (pos) => set({ customCameraPosition: pos }),
  setCustomCameraRotation: (rot) => set({ customCameraRotation: rot }),
  clearCustomCamera: () => set({ customCameraPosition: null, customCameraRotation: null }),
  setDefaultCameraPosition: (pos) => set({ defaultCameraPosition: pos }),
  setDefaultCameraRotation: (rot) => set({ defaultCameraRotation: rot }),
  setLeftCameraPosition: (pos) => set({ leftCameraPosition: pos }),
  setLeftCameraRotation: (rot) => set({ leftCameraRotation: rot }),
  setRightCameraPosition: (pos) => set({ rightCameraPosition: pos }),
  setRightCameraRotation: (rot) => set({ rightCameraRotation: rot }),
  resetCameraPreset: (preset) => {
    if (preset === 'default') set({ defaultCameraPosition: null, defaultCameraRotation: null });
    else if (preset === 'left') set({ leftCameraPosition: null, leftCameraRotation: null });
    else if (preset === 'right') set({ rightCameraPosition: null, rightCameraRotation: null });
  },
  setFlyModeActive: (active) => set({ isFlyModeActive: active }),
  requestSaveCustomCamera: () => set({ saveCustomCameraRequested: true }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setBenchmarkOpen: (open) => set({ benchmarkOpen: open }),
  setAdminUsersOpen: (open) => set({ adminUsersOpen: open }),
  setMannequinSettings: (p) => set(s => ({ mannequinSettings: { ...s.mannequinSettings, ...p } })),
  setGameplaySettings: (p) => set(s => ({ gameplaySettings: { ...s.gameplaySettings, ...p } })),
  setObstacleConfig: (key, update) => set(s => ({
    gameplaySettings: {
      ...s.gameplaySettings,
      obstacleConfigs: {
        ...s.gameplaySettings.obstacleConfigs,
        [key]: { ...s.gameplaySettings.obstacleConfigs[key], ...update },
      },
    },
  })),
  resetMannequinSettings: () => set({ mannequinSettings: { ...DEFAULT_MANNEQUIN } }),
  resetGameplaySettings: () => set({ gameplaySettings: { ...DEFAULT_GAMEPLAY } }),
  setInterfaceSettings: (p) => set(s => ({ interfaceSettings: { ...s.interfaceSettings, ...p } })),
  resetInterfaceSettings: () => set({ interfaceSettings: { ...DEFAULT_INTERFACE } }),
  setParticleSettings: (p) => set(s => ({ particleSettings: { ...s.particleSettings, ...p } })),
  resetParticleSettings: () => set({ particleSettings: { ...DEFAULT_PARTICLES } }),

  // Per-preset performance knobs. New record + new preset object on every write —
  // reference inequality is what drives the persistence subscription below.
  setPerformancePreset: (preset, patch) => set(s => ({
    performanceSettings: {
      ...s.performanceSettings,
      [preset]: {
        ...s.performanceSettings[preset],
        ...patch,
        flowFx: { ...s.performanceSettings[preset].flowFx, ...patch.flowFx },
      },
    },
  })),
  resetPerformancePreset: (preset) => set(s => ({
    performanceSettings: {
      ...s.performanceSettings,
      [preset]: { ...DEFAULT_PERFORMANCE[preset], flowFx: { ...DEFAULT_PERFORMANCE[preset].flowFx } },
    },
  })),
  resetPerformanceSettings: () => set({ performanceSettings: sanitizePerformanceSettings(undefined) }),
  setFileManagerOpen: (open) => set({ fileManagerOpen: open }),
  setEditInfoOpen: (open) => set({ editInfoOpen: open }),
  minimapCam: savedPrefs.minimapCam ?? { height: 7.4, posZ: 1.7, tilt: 40 },
  setMinimapCam: (patch) => set(s => ({ minimapCam: { ...s.minimapCam, ...patch } })),
  minimapCamOpen: false,
  setMinimapCamOpen: (open) => set({ minimapCamOpen: open }),
  minimapZoom: savedPrefs.minimapZoom ?? 1,
  setMinimapZoom: (v) => set({ minimapZoom: Math.max(0.5, Math.min(2, v)) }),
  cameraOptionsOpen: false,
  setCameraOptionsOpen: (open) => set({ cameraOptionsOpen: open }),
  setNewMapOpen: (open) => set({ newMapOpen: open }),
  setLeftSidebarOpen: (open) => set({ leftSidebarOpen: open }),
  setLeftSidebarWidth: (w) => set({ leftSidebarWidth: w }),
  setRightSidebarOpen: (open) => set({ rightSidebarOpen: open }),
  setUiHidden: (hidden) => set({ uiHidden: hidden }),
  setProjectSaved: (v) => set({ projectSaved: v }),
  setRightSidebarWidth: (w) => set({ rightSidebarWidth: w }),
  setBottomPanelOpen: (open) => set({ bottomPanelOpen: open }),
  setBottomPanelHeight: (h) => set({ bottomPanelHeight: h }),

  // ── Note Editing ──────────────────────────────────────────────

  addNote: (note) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Add note');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // Clamp position to grid bounds
    const [cx, cy] = _clampNotePos(state, note.position[0], note.position[1]);
    const clamped: Note = {
      ...note,
      position: [cx, cy, note.position[2]] as [number, number, number],
    };
    const newNotes = [...diff.notes, clamped].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    markPlaced(noteKey(clamped.time_ms, clamped.position[0], clamped.position[1]));
    playPlaceSound();
  },

  addPlacedNote: (note) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    pushUndo(state, state.mirrorMode ? 'Add mirrored note' : 'Add note');
    const [cx, cy] = _clampNotePos(state, note.position[0], note.position[1]);
    const primary: Note = { ...note, position: [cx, cy, note.position[2]] as [number, number, number] };
    const toAdd: Note[] = [primary];
    if (state.mirrorMode) {
      const m: Note = JSON.parse(JSON.stringify(primary));
      if (m.hand === 'right') { m.hand = 'left'; m.note_type = 'left_hand'; }
      else if (m.hand === 'left') { m.hand = 'right'; m.note_type = 'right_hand'; }
      if (m.rail_nodes) {
        m.rail_nodes = m.rail_nodes.map(([x, y, z]: [number, number, number]) =>
          [-x, state.mirrorInvertY ? -y : y, z] as [number, number, number]);
      }
      const my = state.mirrorInvertY ? -primary.position[1] : primary.position[1];
      const [mx2, my2] = _clampNotePos(state, -primary.position[0], my);
      m.position = [mx2, my2, primary.position[2]] as [number, number, number];
      // Skip the mirror if it lands exactly on the primary (centre note, same
      // hand) so a centre placement doesn't stack two identical notes.
      const samePos = Math.abs(mx2 - primary.position[0]) < 1e-4 && Math.abs(my2 - primary.position[1]) < 1e-4;
      const sameHand = m.hand === primary.hand && m.note_type === primary.note_type;
      if (!(samePos && sameHand)) toAdd.push(m);
    }
    // Same-Note-Overwrite: drop any existing note that shares a slot (beat +
    // cell) with one we're about to place, so the new note replaces it.
    let baseNotes = diff.notes;
    if (state.sameNoteOverwrite) {
      const occupies = (n: Note) => toAdd.some(a =>
        Math.abs(n.time_ms - a.time_ms) < 2 &&
        Math.abs(n.position[0] - a.position[0]) < 0.05 &&
        Math.abs(n.position[1] - a.position[1]) < 0.05);
      baseNotes = diff.notes.filter(n => !occupies(n));
    }
    // Hand the appended (unsorted) array to setDiffNotes: it re-sorts AND remaps
    // selectedNoteIds/selectedRailNode by identity, so an existing selection stays
    // valid. (Pre-sorting here would hide the reorder and leave stale indices.)
    setDiffNotes(set, get, [...baseNotes, ...toAdd]);
    markPlaced(noteKey(primary.time_ms, primary.position[0], primary.position[1]));
    playPlaceSound();
  },

  addPlacedSlide: (slide) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // No stacked DUPLICATE walls (reviewer ask): placing the same wall type on
    // an occupied beat toggles it off instead of piling a second copy. Different
    // types on one beat stay allowed — left+right walls etc. are real patterns.
    const EPS_MS = 2;
    if (diff.slides.some(s2 => Math.abs(s2.time_ms - slide.time_ms) < EPS_MS && s2.wall_type === slide.wall_type)) {
      pushUndo(state, 'Remove wall');
      setDiffSlides(set, get, diff.slides.filter(s2 => !(Math.abs(s2.time_ms - slide.time_ms) < EPS_MS && s2.wall_type === slide.wall_type)));
      if (state.selectedSlideIds.length) set({ selectedSlideIds: [] });
      return;
    }
    pushUndo(state, state.mirrorMode ? 'Add mirrored wall' : 'Add wall');
    const toAdd: Slide[] = [slide];
    if (state.mirrorMode) {
      const MIR: Record<string, string> = {
        wall_left: 'wall_right', wall_right: 'wall_left',
        angle_left: 'angle_right', angle_right: 'angle_left',
      };
      const mt = MIR[slide.wall_type];
      if (mt) {
        const m: Slide = { ...slide, wall_type: mt };
        // Console walls carry an explicit position — mirror its X too.
        if (Array.isArray(m.position) && m.position.length) {
          m.position = [-m.position[0], ...m.position.slice(1)];
        }
        toAdd.push(m);
      }
    }
    const newSlides = [...diff.slides, ...toAdd].sort((a, b) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
    // setDiffSlides doesn't remap indices, so a prior slide selection is now stale.
    if (state.selectedSlideIds.length) set({ selectedSlideIds: [] });
  },

  toggleMirrorMode: () => set({ mirrorMode: !get().mirrorMode }),
  toggleMirrorInvertY: () => set({ mirrorInvertY: !get().mirrorInvertY }),

  addNotes: (notes) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Add ${notes.length} notes`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes, ...notes].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
  },

  replaceNotes: (notes) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Replace with ${notes.length} notes`);
    const sorted = [...notes].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, sorted);
    set({ selectedNoteIds: [] });
  },

  removeNotes: (indices) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Remove ${indices.length} notes`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const indexSet = new Set(indices);
    // Emit a particle "explosion" at each deleted note (rendered by the scene),
    // coloured by the note's gameplay colour — paired with the delete SFX.
    const gp = state.gameplaySettings;
    const colorOf = (n: Note): string => {
      const t = (n.note_type || '').toLowerCase();
      if (t.includes('both')) return gp.noteColorBothHands;
      if (t.includes('one') || t.includes('special')) return gp.noteColorOneHand;
      if (n.hand === 'left') return gp.noteColorLeft;
      return gp.noteColorRight;
    };
    for (const idx of indices) {
      const n = diff.notes[idx];
      if (n) emitNoteBurst({ x: n.position[0], y: n.position[1], timeBeats: n.time_beats, color: colorOf(n) });
    }
    const newNotes = diff.notes.filter((_: Note, i: number) => !indexSet.has(i));
    setDiffNotes(set, get, newNotes);
    if (indices.length > 0) playDeleteSound();

    // If the deleted note was the active rail placement target, cancel rail placement
    if (state.railPlacementActive && state.railPlacementNoteIndex !== null && indexSet.has(state.railPlacementNoteIndex)) {
      set({ railPlacementActive: false, railPlacementNoteIndex: null });
    }
    // If the deleted note had a selected rail node, clear selection
    if (state.selectedRailNode && indexSet.has(state.selectedRailNode.noteIndex)) {
      set({ selectedRailNode: null });
    }
    set({ selectedNoteIds: [] });
  },

  updateNote: (index, update) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Update note');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || index >= diff.notes.length) return;
    const newNotes = [...diff.notes];
    let merged = { ...newNotes[index], ...update };
    // Clamp position to grid bounds (safety net)
    if (merged.position) {
      const [cx, cy] = _clampNotePos(state, merged.position[0], merged.position[1]);
      merged = { ...merged, position: [cx, cy, merged.position[2]] as [number, number, number] };
    }
    newNotes[index] = merged;
    setDiffNotes(set, get, newNotes);
  },

  moveSelectedNotes: (updates) => {
    const state = get();
    if (!state.beatmap || updates.length === 0) return;
    pushUndo(state, `Move ${updates.length} note${updates.length === 1 ? '' : 's'}`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes];
    for (const u of updates) {
      if (u.index < 0 || u.index >= newNotes.length) continue;
      const n = { ...newNotes[u.index] };
      // When the note carries a rail, shift its nodes by the SAME delta as the
      // head so the whole rail moves rigidly (preserving its shape) instead of
      // the head detaching. Delta is measured from the note's current position,
      // so it accumulates correctly across per-frame drag updates.
      const hasRail = n.has_rail && !!n.rail_nodes;
      if (u.position) {
        const [cx, cy] = _clampNotePos(state, u.position[0], u.position[1]);
        const dX = cx - n.position[0], dY = cy - n.position[1];
        n.position = [cx, cy, u.position[2]] as [number, number, number];
        if (hasRail && (dX !== 0 || dY !== 0)) {
          n.rail_nodes = n.rail_nodes!.map(([x, y, z]) => [x + dX, y + dY, z] as [number, number, number]);
        }
      }
      if (u.time) {
        const dMs = u.time.ms - n.time_ms;
        n.time_beats = u.time.beats;
        n.time_ms = u.time.ms;
        n.time_seconds = u.time.ms / 1000;
        // Keep position.z in sync with time, exactly as updateNote does.
        n.position = [n.position[0], n.position[1], u.time.ms * 0.02] as [number, number, number];
        if (hasRail && dMs !== 0) {
          // rail node z is encoded time (timeMs * 0.02) — shift by the same dt.
          n.rail_nodes = n.rail_nodes!.map(([x, y, z]) => [x, y, z + dMs * 0.02] as [number, number, number]);
        }
      }
      newNotes[u.index] = n;
    }
    setDiffNotes(set, get, newNotes);
  },

  beginDrag: (label) => {
    _undoSuppressed = false;          // make sure the start snapshot is recorded
    pushUndo(get(), label);
    _undoSuppressed = true;           // then skip every per-frame snapshot
  },
  endDrag: () => { _undoSuppressed = false; },

  mirrorSelected: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    pushUndo(state, 'Mirror notes');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;

    const newNotes = [...diff.notes];
    for (const idx of state.selectedNoteIds) {
      if (idx >= newNotes.length) continue;
      const n = { ...newNotes[idx] };
      n.position = [-n.position[0], n.position[1], n.position[2]] as [number, number, number];
      if (n.hand === 'right') { n.hand = 'left'; n.note_type = 'left_hand'; }
      else if (n.hand === 'left') { n.hand = 'right'; n.note_type = 'right_hand'; }
      if (n.rail_nodes) {
        n.rail_nodes = n.rail_nodes.map(([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]);
      }
      newNotes[idx] = n;
    }
    setDiffNotes(set, get, newNotes);
  },

  flipSelectedY: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    pushUndo(state, 'Flip notes (Y)');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes];
    for (const idx of state.selectedNoteIds) {
      if (idx >= newNotes.length) continue;
      const n = { ...newNotes[idx] };
      // Vertical flip: negate Y only, keep X + handedness (the complement of
      // mirrorSelected, which flips X + swaps hand).
      n.position = [n.position[0], -n.position[1], n.position[2]] as [number, number, number];
      if (n.rail_nodes) {
        n.rail_nodes = n.rail_nodes.map(([x, y, z]: [number, number, number]) => [x, -y, z] as [number, number, number]);
      }
      newNotes[idx] = n;
    }
    setDiffNotes(set, get, newNotes);
  },

  duplicateSelectedOppositeHand: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const additions: Note[] = [];
    for (const idx of state.selectedNoteIds) {
      const src = diff.notes[idx];
      if (!src) continue;
      const dup = JSON.parse(JSON.stringify(src)) as Note;
      // Mirror across X and flip handedness — the copy lands on the opposite
      // hand at the same time, turning a one-hand pattern into two-hand.
      dup.position = [-dup.position[0], dup.position[1], dup.position[2]] as [number, number, number];
      if (dup.hand === 'right') { dup.hand = 'left'; dup.note_type = 'left_hand'; }
      else if (dup.hand === 'left') { dup.hand = 'right'; dup.note_type = 'right_hand'; }
      if (dup.rail_nodes) {
        dup.rail_nodes = dup.rail_nodes.map(([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]);
      }
      additions.push(dup);
    }
    if (additions.length === 0) return;
    pushUndo(state, `Duplicate ${additions.length} to opposite hand`);
    const newNotes = [...diff.notes, ...additions].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });   // indices shifted by the sort
  },

  scaleSelection: (factor) => {
    const state = get();
    if (!state.beatmap || !isFinite(factor) || factor <= 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const bpm = state.beatmap.bpm || 120;
    const sel = state.selectedNoteIds.filter(i => i < diff.notes.length);
    if (sel.length < 2) return;   // need a span to scale
    // Anchor on the earliest selected note; scale every selected note's time
    // away from (or toward) it. time_beats/time_seconds are re-derived from the
    // new chart-relative time_ms so the offset/beat frame stays consistent.
    const anchorMs = Math.min(...sel.map(i => diff.notes[i].time_ms));
    pushUndo(state, factor > 1 ? 'Expand selection' : 'Compress selection');
    const selSet = new Set(sel);
    const newNotes = diff.notes
      .map((n, i) => {
        if (!selSet.has(i)) return n;
        const newMs = anchorMs + (n.time_ms - anchorMs) * factor;
        const scaled: Note = {
          ...n,
          time_ms: newMs,
          time_seconds: newMs / 1000,
          time_beats: (newMs / 1000) * (bpm / 60),
          position: [n.position[0], n.position[1], newMs * 0.02] as [number, number, number],
        };
        // Scale the rail's node times by the SAME factor around the anchor, so the
        // whole rail stretches with its head and the head stays before its nodes.
        if (n.has_rail && n.rail_nodes) {
          scaled.rail_nodes = n.rail_nodes.map(([x, y, z]) => {
            const newNodeMs = anchorMs + (z / 0.02 - anchorMs) * factor;
            return [x, y, newNodeMs * 0.02] as [number, number, number];
          });
        }
        return scaled;
      })
      .sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });   // indices shifted by the re-sort
  },

  deleteAllObjects: () => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const isEmpty =
      diff.notes.length === 0 && diff.slides.length === 0 &&
      diff.crouches.length === 0 && diff.jumps.length === 0 &&
      !diff.squares?.length && !diff.triangles?.length && !diff.crouch_obstacles?.length;
    if (isEmpty) return;
    pushUndo(state, 'Delete all objects');
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: {
            ...diff,
            notes: [], slides: [], crouches: [], jumps: [],
            // Only reset the optional shape arrays when they exist, so a VR map
            // keeps its absent shape (undefined) rather than growing empty ones.
            ...(diff.crouch_obstacles ? { crouch_obstacles: [] } : {}),
            ...(diff.squares ? { squares: [] } : {}),
            ...(diff.triangles ? { triangles: [] } : {}),
            note_count: 0, rail_count: 0, slide_count: 0,
            crouch_count: 0, jump_count: 0,
            duration_beats: 0, duration_seconds: 0,
          },
        },
      },
      selectedNoteIds: [],
      selectedSlideIds: [],
      selectedRailNode: null,
      railPlacementActive: false,
      railPlacementNoteIndex: null,
    }));
  },

  joinNotesToRail: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length < 2) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const sel = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => ({ i, n: diff.notes[i] }))
      .sort((a, b) => a.n.time_ms - b.n.time_ms);
    if (sel.length < 2) return;
    const head = sel[0].n;
    const rest = sel.slice(1);
    // A rail is single-hand — only join notes that share the head's hand.
    if (!rest.every(r => r.n.hand === head.hand)) return;
    // A node must sit STRICTLY after the head in time; drop any selected note that
    // shares the head's beat (a stacked / two-hand pair) so it can't become a
    // degenerate node coinciding exactly with the head.
    const restNodes = rest.filter(r => r.n.time_ms > head.time_ms);
    if (restNodes.length === 0) return;
    pushUndo(state, 'Join notes into rail');
    // rail_nodes carry ENCODED time (timeMs * 0.02); the head stays the note's
    // own position, every other selected note becomes a subsequent node.
    const nodes: [number, number, number][] = [...(head.rail_nodes ?? [])];
    for (const r of restNodes) {
      nodes.push([r.n.position[0], r.n.position[1], r.n.time_ms * 0.02]);
      if (r.n.rail_nodes) nodes.push(...r.n.rail_nodes);   // absorb existing sub-rails
    }
    nodes.sort((a, b) => a[2] - b[2]);
    const consumed = new Set(restNodes.map(r => r.i));
    const newNotes = diff.notes
      .map((n, idx) => idx === sel[0].i ? { ...n, has_rail: true, rail_nodes: nodes } : n)
      .filter((_, idx) => !consumed.has(idx));
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });
  },

  splitRailAtNode: () => {
    const state = get();
    const sel = state.selectedRailNode;
    if (!state.beatmap || !sel) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const note = diff.notes[sel.noteIndex];
    if (!note || !note.has_rail || !note.rail_nodes || sel.nodeIndex >= note.rail_nodes.length) return;
    pushUndo(state, 'Split rail');
    const bpm = state.beatmap.bpm || 120;
    const rn = note.rail_nodes;
    const k = sel.nodeIndex;
    const splitNode = rn[k];
    const before = rn.slice(0, k);
    const after = rn.slice(k + 1);
    const timeMs = splitNode[2] / 0.02;
    // Original rail keeps the nodes before the split; the split node becomes the
    // head of a new rail carrying the nodes after it.
    const orig: Note = {
      ...note,
      has_rail: before.length > 0,
      rail_nodes: before.length > 0 ? before : null,
    };
    const head2: Note = {
      ...note,
      time_ms: timeMs,
      time_seconds: timeMs / 1000,
      time_beats: (timeMs / 1000) * (bpm / 60),
      position: [splitNode[0], splitNode[1], splitNode[2]] as [number, number, number],
      has_rail: after.length > 0,
      rail_nodes: after.length > 0 ? after : null,
      note_id: '',
    };
    const newNotes = diff.notes.map((n, idx) => idx === sel.noteIndex ? orig : n);
    newNotes.push(head2);
    newNotes.sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedRailNode: null, selectedNoteIds: [] });
  },

  recalcRailNodesByStep: () => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return;
    // Target: the selected rail node's note, else a single selected note w/ a rail.
    let noteIndex = -1;
    if (state.selectedRailNode) noteIndex = state.selectedRailNode.noteIndex;
    else if (state.selectedNoteIds.length === 1) noteIndex = state.selectedNoteIds[0];
    if (noteIndex < 0 || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return;
    const bpm = state.beatmap.bpm || 120;
    const step = state.scrollStep || 0.25;
    // node[2] is encoded time (timeMs * 0.02); convert to/from chart beats purely
    // (offset-free) to match the renderer's decode.
    const decodeBeat = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
    const encTime = (beat: number) => (beat * 60000 / bpm) * 0.02;
    // Control points in beat-space: the head, then every node.
    const ctrl = [
      { b: note.time_beats, x: note.position[0], y: note.position[1] },
      ...note.rail_nodes.map(n => ({ b: decodeBeat(n[2]), x: n[0], y: n[1] })),
    ].sort((a, b) => a.b - b.b);
    const startBeat = ctrl[0].b, endBeat = ctrl[ctrl.length - 1].b;
    if (endBeat - startBeat < 1e-4) return;
    // Uniform Catmull-Rom (tension 0.5) on one axis.
    const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const t2 = t * t, t3 = t2 * t;
      return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
    };
    const sampleAt = (beat: number) => {
      let i = 0;
      while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
      const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
      const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
      const seg = Math.max(1e-6, p2.b - p1.b);
      const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
      return { x: cr(p0.x, p1.x, p2.x, p3.x, t), y: cr(p0.y, p1.y, p2.y, p3.y, t) };
    };
    pushUndo(state, 'Recalc rail nodes');
    const newNodes: [number, number, number][] = [];
    for (let b = startBeat + step; b < endBeat - 1e-4; b += step) {
      const p = sampleAt(b);
      newNodes.push([p.x, p.y, encTime(b)]);
    }
    // Keep the exact endpoint so the rail preserves its length.
    const last = ctrl[ctrl.length - 1];
    newNodes.push([last.x, last.y, encTime(endBeat)]);
    const newNotes = diff.notes.map((n, idx) => idx === noteIndex ? { ...n, rail_nodes: newNodes } : n);
    setDiffNotes(set, get, newNotes);
  },

  placeWallAtPlayhead: (wallType) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const beat = state.playback.currentBeat;
    const timeMs = get().beatToTimeMs(beat);
    if (wallType === 'crouch') { get().addCrouch(timeMs); return; }
    if (wallType === 'square' || wallType === 'triangle') {
      const ob: Obstacle = {
        time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
        position: [0, 0, 0], z_rotation: 0, initialized: true,
      };
      const key = wallType === 'square' ? 'squares' : 'triangles';
      const arr = [...(diff[key] ?? []), ob].sort((a, b) => a.time_ms - b.time_ms);
      pushUndo(state, `Add ${wallType}`);
      set(() => ({
        beatmap: {
          ...state.beatmap!,
          difficulties: {
            ...state.beatmap!.difficulties,
            [state.activeDifficulty]: { ...diff, [key]: arr },
          },
        },
      }));
      return;
    }
    // Slide walls (left/right/center/angle_*) — mirror-aware.
    get().addPlacedSlide({
      time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
      slide_type: 0, wall_type: wallType, initialized: true,
    });
  },

  placeNoteAtPlayhead: () => {
    const state = get();
    if (!state.beatmap) return;
    const HAND: Record<string, { hand: 'left' | 'right' | 'special'; note_type: string }> = {
      note_left: { hand: 'left', note_type: 'left_hand' },
      note_right: { hand: 'right', note_type: 'right_hand' },
      note_onehand: { hand: 'special', note_type: 'one_hand_special' },
      note_bothhands: { hand: 'special', note_type: 'both_hands_special' },
      rail_left: { hand: 'left', note_type: 'left_hand' },
      rail_right: { hand: 'right', note_type: 'right_hand' },
      rail_onehand: { hand: 'special', note_type: 'one_hand_special' },
      rail_bothhands: { hand: 'special', note_type: 'both_hands_special' },
    };
    const map = HAND[state.toolMode];
    if (!map) return;   // only meaningful while a note/rail tool is active
    const beat = state.playback.currentBeat;
    const timeMs = get().beatToTimeMs(beat);
    get().addPlacedNote({
      time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
      position: [0, 0, timeMs * 0.02],
      note_type: map.note_type, hand: map.hand,
      direction: 0, has_rail: false, rail_nodes: null,
      combo_id: 0, note_id: '',
    });
  },

  toggleSameNoteOverwrite: () => set({ sameNoteOverwrite: !get().sameNoteOverwrite }),

  snipRailAtPlayhead: () => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return;
    let noteIndex = -1;
    if (state.selectedRailNode) noteIndex = state.selectedRailNode.noteIndex;
    else if (state.selectedNoteIds.length === 1) noteIndex = state.selectedNoteIds[0];
    if (noteIndex < 0 || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return;
    const bpm = state.beatmap.bpm || 120;
    const decodeBeat = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
    const encTime = (b: number) => (b * 60000 / bpm) * 0.02;
    const beat = state.playback.currentBeat;
    const ctrl = [
      { b: note.time_beats, x: note.position[0], y: note.position[1] },
      ...note.rail_nodes.map(n => ({ b: decodeBeat(n[2]), x: n[0], y: n[1] })),
    ].sort((a, b2) => a.b - b2.b);
    const startBeat = ctrl[0].b, endBeat = ctrl[ctrl.length - 1].b;
    if (beat <= startBeat + 1e-3 || beat >= endBeat - 1e-3) return;   // only inside the span
    const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const t2 = t * t, t3 = t2 * t;
      return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
    };
    let i = 0;
    while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
    const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
    const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
    const seg = Math.max(1e-6, p2.b - p1.b);
    const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
    const newNode: [number, number, number] = [cr(p0.x, p1.x, p2.x, p3.x, t), cr(p0.y, p1.y, p2.y, p3.y, t), encTime(beat)];
    const nodes = [...note.rail_nodes];
    let insertIdx = nodes.length;
    for (let j = 0; j < nodes.length; j++) { if (newNode[2] < nodes[j][2]) { insertIdx = j; break; } }
    nodes.splice(insertIdx, 0, newNode);
    pushUndo(state, 'Snip rail (add node)');
    const newNotes = diff.notes.map((n, idx) => idx === noteIndex ? { ...n, rail_nodes: nodes } : n);
    setDiffNotes(set, get, newNotes);
    // The inserted node shifts every node at/after insertIdx up by one — keep a
    // selected node on this same rail pointing at the node it was before.
    const selRN = state.selectedRailNode;
    if (selRN && selRN.noteIndex === noteIndex && insertIdx <= selRN.nodeIndex) {
      set({ selectedRailNode: { ...selRN, nodeIndex: selRN.nodeIndex + 1 } });
    }
  },

  cycleSelectedNoteType: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const ORDER: { hand: 'left' | 'right' | 'special'; note_type: string }[] = [
      { hand: 'right', note_type: 'right_hand' },
      { hand: 'left', note_type: 'left_hand' },
      { hand: 'special', note_type: 'one_hand_special' },
      { hand: 'special', note_type: 'both_hands_special' },
    ];
    pushUndo(state, 'Cycle note type');
    const sel = new Set(state.selectedNoteIds);
    const newNotes = diff.notes.map((n, idx) => {
      if (!sel.has(idx)) return n;
      const cur = ORDER.findIndex(o => o.note_type === n.note_type);
      const nxt = ORDER[(cur + 1) % ORDER.length];   // cur === -1 → ORDER[0]
      return { ...n, hand: nxt.hand, note_type: nxt.note_type };
    });
    setDiffNotes(set, get, newNotes);
  },

  snapSelectionToRail: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const bpm = state.beatmap.bpm || 120;
    const selSet = new Set(state.selectedNoteIds);
    let changed = false;
    const newNotes = diff.notes.map((n, idx) => {
      if (!selSet.has(idx)) return n;
      // Nearest rail whose span covers this note's beat (excluding itself).
      let best: { x: number; y: number; d: number } | null = null;
      diff.notes.forEach((r, ri) => {
        if (ri === idx || !r.has_rail) return;
        const xy = _railXYAtBeat(r, n.time_beats, bpm);
        if (!xy) return;
        const d = Math.hypot(xy[0] - n.position[0], xy[1] - n.position[1]);
        if (!best || d < best.d) best = { x: xy[0], y: xy[1], d };
      });
      if (!best) return n;
      changed = true;
      const b = best as { x: number; y: number; d: number };
      return { ...n, position: [b.x, b.y, n.position[2]] as [number, number, number] };
    });
    if (!changed) return;
    pushUndo(state, 'Snap to rail');
    setDiffNotes(set, get, newNotes);
  },

  // ── Slide Editing ──────────────────────────────────────────────

  addSlide: (slide) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Add slide');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = [...diff.slides, slide].sort((a, b) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
  },

  removeSlide: (index) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove slide');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = diff.slides.filter((_: Slide, i: number) => i !== index);
    setDiffSlides(set, get, newSlides);
    set({ selectedSlideIds: [] });
  },

  removeSlides: (indices) => {
    const state = get();
    if (!state.beatmap || indices.length === 0) return;
    pushUndo(state, `Remove ${indices.length} obstacles`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const drop = new Set(indices);
    const newSlides = diff.slides.filter((_: Slide, i: number) => !drop.has(i));
    setDiffSlides(set, get, newSlides);
    set({ selectedSlideIds: [] });
  },

  // In-place obstacle edit (no re-sort, so the gizmo's index stays stable during
  // a drag; call sortSlides() once when the drag ends).
  updateSlide: (index, patch) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || index < 0 || index >= diff.slides.length) return;
    const newSlides = [...diff.slides];
    newSlides[index] = { ...newSlides[index], ...patch };
    setDiffSlides(set, get, newSlides);
  },

  sortSlides: () => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = [...diff.slides].sort((a: Slide, b: Slide) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
  },

  // ── Jump / Crouch Editing ─────────────────────────────────────

  addJump: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Add jump');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newJumps = [...diff.jumps, timeMs].sort((a, b) => a - b);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, jumps: newJumps, jump_count: newJumps.length },
        },
      },
    }));
  },

  removeJump: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove jump');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.jumps.indexOf(timeMs);
    if (idx === -1) return;
    const newJumps = diff.jumps.filter((_: number, i: number) => i !== idx);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, jumps: newJumps, jump_count: newJumps.length },
        },
      },
    }));
  },

  addCrouch: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Add crouch');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newCrouches = [...diff.crouches, timeMs].sort((a, b) => a - b);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, crouches: newCrouches, crouch_count: newCrouches.length },
        },
      },
    }));
  },

  removeCrouch: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove crouch');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.crouches.indexOf(timeMs);
    if (idx === -1) return;
    const newCrouches = diff.crouches.filter((_: number, i: number) => i !== idx);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, crouches: newCrouches, crouch_count: newCrouches.length },
        },
      },
    }));
  },

  // Move a crouch / jump in time (drag). Identified by value; replace + re-sort.
  moveCrouch: (oldMs, newMs) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.crouches.indexOf(oldMs);
    if (idx === -1) return;
    const arr = [...diff.crouches];
    arr[idx] = newMs;
    arr.sort((a: number, b: number) => a - b);
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: { ...diff, crouches: arr },
      } },
    }));
  },

  moveJump: (oldMs, newMs) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.jumps.indexOf(oldMs);
    if (idx === -1) return;
    const arr = [...diff.jumps];
    arr[idx] = newMs;
    arr.sort((a: number, b: number) => a - b);
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: { ...diff, jumps: arr },
      } },
    }));
  },

  toggleJumpAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    const bpm = state.beatmap.bpm;
    const timeMs = Math.round((beat / (bpm / 60)) * 1000);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // Check if a jump exists near this time (within 10ms)
    const existing = diff.jumps.findIndex((t: number) => Math.abs(t - timeMs) < 10);
    if (existing >= 0) {
      get().removeJump(diff.jumps[existing]);
    } else {
      get().addJump(timeMs);
    }
  },

  toggleCrouchAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    const bpm = state.beatmap.bpm;
    const timeMs = Math.round((beat / (bpm / 60)) * 1000);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const existing = diff.crouches.findIndex((t: number) => Math.abs(t - timeMs) < 10);
    if (existing >= 0) {
      get().removeCrouch(diff.crouches[existing]);
    } else {
      get().addCrouch(timeMs);
    }
  },

  // ── Undo/Redo ─────────────────────────────────────────────────

  undo: () => {
    const { undoStack, beatmap, redoStack } = get();
    if (undoStack.length === 0 || !beatmap) return;
    const prev = undoStack[undoStack.length - 1];
    set({
      redoStack: [...redoStack, { label: 'redo', difficulties: { ...beatmap.difficulties } }],
      undoStack: undoStack.slice(0, -1),
      beatmap: { ...beatmap, difficulties: prev.difficulties },
      selectedNoteIds: [],
    });
  },

  redo: () => {
    const { redoStack, beatmap, undoStack } = get();
    if (redoStack.length === 0 || !beatmap) return;
    const next = redoStack[redoStack.length - 1];
    set({
      undoStack: [...undoStack, { label: 'undo', difficulties: { ...beatmap.difficulties } }],
      redoStack: redoStack.slice(0, -1),
      beatmap: { ...beatmap, difficulties: next.difficulties },
      selectedNoteIds: [],
    });
  },

  // ── Keybinding Methods ───────────────────────────────────────

  setKeybinding: (id, slot, combo) => {
    const bindings = [...get().keybindings];
    const idx = bindings.findIndex(b => b.id === id);
    if (idx === -1) return;
    bindings[idx] = { ...bindings[idx], [slot]: combo };
    set({ keybindings: bindings });
    try { localStorage.setItem('abm-keybindings', JSON.stringify(bindings)); } catch { /* ignore */ }
  },

  resetKeybindings: () => {
    const bindings = [...DEFAULT_KEYBINDINGS];
    set({ keybindings: bindings });
    try { localStorage.setItem('abm-keybindings', JSON.stringify(bindings)); } catch { /* ignore */ }
  },

  setKeybindingsOpen: (open) => set({ keybindingsOpen: open }),

  // ── Clipboard Methods ──────────────────────────────────────────

  copySelected: () => {
    const state = get();
    if (!state.beatmap) return;
    if (state.selectedNoteIds.length === 0 && state.selectedSlideIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const notes = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => JSON.parse(JSON.stringify(diff.notes[i])) as Note);
    const slides = state.selectedSlideIds
      .filter(i => i < (diff.slides?.length ?? 0))
      .map(i => JSON.parse(JSON.stringify(diff.slides[i])) as Slide);
    set({ clipboard: notes, clipboardSlides: slides, clipboardBpm: state.beatmap.bpm });
  },

  cutSelected: () => {
    const state = get();
    if (!state.beatmap) return;
    if (state.selectedNoteIds.length === 0 && state.selectedSlideIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const notes = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => JSON.parse(JSON.stringify(diff.notes[i])) as Note);
    const slides = state.selectedSlideIds
      .filter(i => i < (diff.slides?.length ?? 0))
      .map(i => JSON.parse(JSON.stringify(diff.slides[i])) as Slide);
    set({ clipboard: notes, clipboardSlides: slides, clipboardBpm: state.beatmap.bpm });
    if (notes.length) get().removeNotes([...state.selectedNoteIds]);
    if (slides.length) get().removeSlides([...state.selectedSlideIds]);
  },

  pasteAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    if (state.clipboard.length === 0 && state.clipboardSlides.length === 0) return;
    const bpm = state.beatmap.bpm;
    // Earliest beat across BOTH lists, so notes and walls keep their relative
    // spacing when a mixed selection is pasted.
    const beatOffset = beat - _clipboardMinBeat(state.clipboard, state.clipboardSlides);
    const newNotes = state.clipboard.map(n => {
      const newBeat = n.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      return {
        ...JSON.parse(JSON.stringify(n)),
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeMs / 1000,
      } as Note;
    });
    const newSlides = state.clipboardSlides.map(s => {
      const newBeat = s.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      return {
        ...JSON.parse(JSON.stringify(s)),
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeMs / 1000,
      } as Slide;
    });
    if (newNotes.length) get().addNotes(newNotes);
    for (const s of newSlides) get().addPlacedSlide(s);
  },

  pasteMirrorAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    if (state.clipboard.length === 0 && state.clipboardSlides.length === 0) return;
    const bpm = state.beatmap.bpm;
    const beatOffset = beat - _clipboardMinBeat(state.clipboard, state.clipboardSlides);
    const newNotes = state.clipboard.map(n => {
      const newBeat = n.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      const mirrored = JSON.parse(JSON.stringify(n)) as Note;
      mirrored.time_beats = newBeat;
      mirrored.time_ms = timeMs;
      mirrored.time_seconds = timeMs / 1000;
      mirrored.position = [-mirrored.position[0], mirrored.position[1], mirrored.position[2]];
      if (mirrored.hand === 'right') { mirrored.hand = 'left'; mirrored.note_type = 'left_hand'; }
      else if (mirrored.hand === 'left') { mirrored.hand = 'right'; mirrored.note_type = 'right_hand'; }
      if (mirrored.rail_nodes) {
        mirrored.rail_nodes = mirrored.rail_nodes.map(
          ([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]
        );
      }
      return mirrored;
    });
    const newSlides = state.clipboardSlides.map(s => {
      const newBeat = s.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      const mirrored = JSON.parse(JSON.stringify(s)) as Slide;
      mirrored.time_beats = newBeat;
      mirrored.time_ms = timeMs;
      mirrored.time_seconds = timeMs / 1000;
      if (mirrored.position && mirrored.position.length >= 1) {
        mirrored.position = [-(mirrored.position[0] ?? 0), ...mirrored.position.slice(1)];
      }
      // A side-locked wall must also swap sides, or the mirror keeps the original
      // lane's mesh.
      mirrored.wall_type = _MIRROR_WALL_TYPE[mirrored.wall_type] ?? mirrored.wall_type;
      return mirrored;
    });
    if (newNotes.length) get().addNotes(newNotes);
    for (const s of newSlides) get().addPlacedSlide(s);
  },

  // ── Grid Methods ───────────────────────────────────────────────

  setGridVisible: (v) => set({ gridVisible: v }),
  setGridSize: (s) => set({ gridSize: s }),
  setSnapToGrid: (v) => set({ snapToGrid: v }),
  setGridGuideType: (t) => set({ gridGuideType: t }),
  setGridGuideVisible: (v) => set({ gridGuideVisible: v }),
  setGridWidth: (w) => set({ gridWidth: w }),
  setGridHeight: (h) => set({ gridHeight: h }),
  setGridDivisionsH: (d) => set({ gridDivisionsH: d }),
  setGridDivisionsV: (d) => set({ gridDivisionsV: d }),
  setGridColor: (c) => set({ gridColor: c }),
  setGridOpacity: (o) => set({ gridOpacity: o }),
  setNotesGridYOffset: (v) => set({ notesGridYOffset: v }),

  snapToGridPosition: (x, y) => {
    const { snapToGrid, gridWidth, gridHeight, gridDivisionsH, gridDivisionsV } = get();
    if (!snapToGrid) return [x, y];
    // x,y are in note-space. Grid dimensions are in world units.
    // Conversion: worldPos = notePos * GRID_SCALE * 10 (where GRID_SCALE = 0.1365)
    const SCALE = 0.1365 * 10; // = 1.365
    // Grid cell size in note-space
    const cellW = (gridWidth / gridDivisionsH) / SCALE;
    const cellH = (gridHeight / gridDivisionsV) / SCALE;
    // Snap to nearest grid intersection
    const snapX = Math.round(x / cellW) * cellW;
    const snapY = Math.round(y / cellH) * cellH;
    // Clamp to grid bounds (in note-space)
    const halfW = (gridWidth / 2) / SCALE;
    const halfH = (gridHeight / 2) / SCALE;
    return [
      Math.max(-halfW, Math.min(halfW, snapX)),
      Math.max(-halfH, Math.min(halfH, snapY)),
    ];
  },

  // ── Rail Placement Methods ─────────────────────────────────────

  startRailPlacement: (noteIndex) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    // Enable rail on this note if not already
    if (!note.has_rail) {
      get().updateNote(noteIndex, { has_rail: true, rail_nodes: [] });
    }
    set({ railPlacementActive: true, railPlacementNoteIndex: noteIndex });
  },

  addRailNode: (position) => {
    const state = get();
    if (!state.railPlacementActive || state.railPlacementNoteIndex === null) return;
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff) return;
    const noteIndex = state.railPlacementNoteIndex;
    if (noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];

    // Enforce forward-only: new node must be after the head note in time
    // position[2] = timeMs * 0.02, note.time_ms is in ms
    const newTimeMs = position[2] / 0.02;
    if (newTimeMs <= note.time_ms) return; // reject backward nodes

    // Insert in sorted position by time (Z component) so mid-rail insertions work
    const nodes = note.rail_nodes ? [...note.rail_nodes] : [];
    let insertIdx = nodes.length; // default: append at end
    for (let i = 0; i < nodes.length; i++) {
      if (position[2] < nodes[i][2]) {
        insertIdx = i;
        break;
      }
    }
    nodes.splice(insertIdx, 0, position);
    get().updateNote(noteIndex, { rail_nodes: nodes });
  },

  finishRailPlacement: () => {
    set({ railPlacementActive: false, railPlacementNoteIndex: null });
  },

  cancelRailPlacement: () => {
    const state = get();
    if (state.railPlacementNoteIndex !== null) {
      const diff = state.beatmap?.difficulties[state.activeDifficulty];
      if (diff && state.railPlacementNoteIndex < diff.notes.length) {
        const note = diff.notes[state.railPlacementNoteIndex];
        if (note.rail_nodes && note.rail_nodes.length === 0) {
          get().updateNote(state.railPlacementNoteIndex, { has_rail: false, rail_nodes: null });
        }
      }
    }
    set({ railPlacementActive: false, railPlacementNoteIndex: null });
  },

  // ── Note Dragging ──────────────────────────────────────────────

  startDrag: (index) => {
    const diff = get().getCurrentDifficulty();
    if (!diff || index >= diff.notes.length) return;
    set({ draggingNoteIndex: index, selectedNoteIds: [index] });
  },

  finishDrag: (newBeat, newX, newY) => {
    const state = get();
    const idx = state.draggingNoteIndex;
    if (idx === null) return;
    const diff = state.getCurrentDifficulty();
    if (!diff || idx >= diff.notes.length) return;

    const bpm = state.beatmap?.bpm || 120;
    const timeMs = (newBeat / (bpm / 60)) * 1000;
    const timeSeconds = timeMs / 1000;

    // If multiple notes are selected, compute the delta and apply to all
    const selectedIds = state.selectedNoteIds;
    if (selectedIds.length > 1 && selectedIds.includes(idx)) {
      const origNote = diff.notes[idx];
      const dBeat = newBeat - origNote.time_beats;
      const dX = newX - origNote.position[0];
      const dY = newY - origNote.position[1];

      pushUndo(state, `Move ${selectedIds.length} notes`);
      const newNotes = [...diff.notes];
      for (const id of selectedIds) {
        if (id >= newNotes.length) continue;
        const n = newNotes[id];
        const nBeat = n.time_beats + dBeat;
        const nTimeMs = (nBeat / (bpm / 60)) * 1000;
        const [cx, cy] = _clampNotePos(state, n.position[0] + dX, n.position[1] + dY);
        newNotes[id] = {
          ...n,
          time_beats: nBeat,
          time_ms: nTimeMs,
          time_seconds: nTimeMs / 1000,
          position: [cx, cy, nTimeMs * 0.02] as [number, number, number],
        };
      }
      newNotes.sort((a, b) => a.time_ms - b.time_ms);
      setDiffNotes(set, get, newNotes);
    } else {
      // Single note drag
      pushUndo(state, 'Move note');
      const newNotes = [...diff.notes];
      const [cx, cy] = _clampNotePos(state, newX, newY);
      newNotes[idx] = {
        ...newNotes[idx],
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeSeconds,
        position: [cx, cy, timeMs * 0.02] as [number, number, number],
      };
      newNotes.sort((a, b) => a.time_ms - b.time_ms);
      setDiffNotes(set, get, newNotes);
    }

    set({ draggingNoteIndex: null });
  },

  cancelDrag: () => {
    set({ draggingNoteIndex: null });
  },

  // ── Bookmarks (named, global) ──────────────────────────────────

  addBookmark: (beat, name) => {
    const bm = get().bookmarks;
    // Don't add duplicate near this beat (within 0.01 tolerance)
    if (bm.some(b => Math.abs(b.beat - beat) < 0.01)) return;
    const next = [...bm, { beat, name }].sort((a, b) => a.beat - b.beat);
    set({ bookmarks: next });
    saveBookmarkDraft(get().beatmap?.id, next);
  },

  removeBookmark: (beat) => {
    const state = get();
    const editing = state.editingBookmarkBeat != null && Math.abs(state.editingBookmarkBeat - beat) < 0.01
      ? null : state.editingBookmarkBeat;
    const next = state.bookmarks.filter(b => Math.abs(b.beat - beat) >= 0.01);
    set({ bookmarks: next, editingBookmarkBeat: editing });
    saveBookmarkDraft(state.beatmap?.id, next);
  },

  updateBookmarkName: (beat, name) => {
    const next = get().bookmarks.map(b => Math.abs(b.beat - beat) < 0.01 ? { ...b, name } : b);
    set({ bookmarks: next });
    saveBookmarkDraft(get().beatmap?.id, next);
  },

  moveBookmark: (oldBeat, newBeat) => {
    const state = get();
    const bm = state.bookmarks;
    const idx = bm.findIndex(b => Math.abs(b.beat - oldBeat) < 0.01);
    if (idx < 0) return;
    if (bm.some((b, i) => i !== idx && Math.abs(b.beat - newBeat) < 0.01)) return; // don't stack
    const next = bm.map((b, i) => (i === idx ? { ...b, beat: newBeat } : b)).sort((a, b) => a.beat - b.beat);
    const wasEditing = state.editingBookmarkBeat != null && Math.abs(state.editingBookmarkBeat - oldBeat) < 0.01;
    set({ bookmarks: next, ...(wasEditing ? { editingBookmarkBeat: newBeat } : {}) });
    saveBookmarkDraft(state.beatmap?.id, next);
  },

  clearBookmarks: () => {
    set({ bookmarks: [], editingBookmarkBeat: null });
    saveBookmarkDraft(get().beatmap?.id, []);
  },

  setBookmarkDialogOpen: (open) => set({ bookmarkDialogOpen: open }),

  setEditingBookmarkBeat: (beat) => set({ editingBookmarkBeat: beat }),

  jumpToNextBookmark: () => {
    const { bookmarks, playback, beatmap } = get();
    if (bookmarks.length === 0 || !beatmap) return;
    const current = playback.currentBeat;
    const next = bookmarks.find(b => b.beat > current + 0.01);
    if (next !== undefined) {
      const bpm = beatmap.bpm || 120;
      const offset = beatmap.offset || 0;
      const time = (next.beat / (bpm / 60)) + (offset / 1000);
      get().setCurrentTime(Math.max(0, time));
    }
  },

  jumpToPrevBookmark: () => {
    const { bookmarks, playback, beatmap } = get();
    if (bookmarks.length === 0 || !beatmap) return;
    const current = playback.currentBeat;
    const prev = [...bookmarks].reverse().find(b => b.beat < current - 0.01);
    if (prev !== undefined) {
      const bpm = beatmap.bpm || 120;
      const offset = beatmap.offset || 0;
      const time = (prev.beat / (bpm / 60)) + (offset / 1000);
      get().setCurrentTime(Math.max(0, time));
    }
  },

  // ── Flash Events (global light effects) ────────────────────────

  // ── Shapes ────────────────────────────────────────────────
  // Design has its OWN undo/redo history (separate from the note/slide history)
  // since it's a separate, localStorage-persisted layer. Continuous slider edits
  // to the same group coalesce into one history step (pushDesignUndo below).

  // My Library — save/reuse wall arts across projects. Server-backed (per-user
  // ownership + public sharing). The server is the source of truth; we do NOT
  // mirror it to localStorage — a shared browser must never cache one user's
  // shapes where another user could see them. On a server blip we keep the
  // in-memory state rather than blanking the panel.
  loadDesignLibraryFromServer: async () => {
    // One-time migration: upload the pre-existing localStorage-only library as
    // the current user's private shapes. Per-shape best-effort so one shape that
    // can't upload (e.g. an id already owned by someone else → 403) never blocks
    // the batch. The flag is set only once the server is confirmed reachable
    // (listShapes below succeeds), so a full outage safely retries next open
    // without losing the local copy (still under DESIGN_LIB_KEY).
    const needMigrate = !localStorage.getItem(DESIGN_LIB_MIGRATED_KEY);
    if (needMigrate) {
      for (const s of loadDesignLibrary()) {
        try { await saveShape(s); } catch { /* skip a shape that won't upload */ }
      }
    }
    try {
      const shapes = await listShapes();
      set({ designLibrary: shapes });
      if (needMigrate) localStorage.setItem(DESIGN_LIB_MIGRATED_KEY, '1');
    } catch {
      // Server unreachable — keep whatever is already in memory; retry next open.
    }
  },
  addToDesignLibrary: (groupId) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const saved = makeSavedShape(g, g.name || '');
    // Optimistic: show it immediately, then persist server-side and refresh.
    const lib = [{ ...saved, visibility: 'private' as const, is_owner: true }, ...state.designLibrary];
    set({ designLibrary: lib });
    void (async () => {
      try {
        await saveShape(saved);
        await get().loadDesignLibraryFromServer();
      } catch { /* offline — the optimistic in-memory entry stands in */ }
    })();
  },
  removeFromDesignLibrary: (id) => {
    const lib = get().designLibrary.filter(s => s.id !== id);
    set({ designLibrary: lib });
    void deleteShape(id).catch(() => { /* offline — local removal stands */ });
  },
  setLibraryShapeVisibility: async (id, isPublic) => {
    await setShapeVisibility(id, isPublic);
    const lib = get().designLibrary.map(s =>
      (s.id === id ? { ...s, visibility: (isPublic ? 'public' : 'private') as 'private' | 'public' } : s));
    set({ designLibrary: lib });
  },
  addLibraryArtToMap: (id) => {
    const state = get();
    const saved = state.designLibrary.find(s => s.id === id);
    if (!saved) return;
    pushDesignUndo(state, true);
    const g = instantiateSavedShape(saved, state.playback.currentBeat);
    const next = [...state.designShapes, g];
    set({ designShapes: next, selectedDesignShapeIds: [g.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },

  addDesignShape: () => {
    const state = get();
    pushDesignUndo(state, true);
    // New decor comes up in a random vivid neon colour so it pops against the dark
    // stage out of the box (the panel can retint it).
    const group = makeDesignShape(state.playback.currentBeat, randomNeonColor());
    const next = [...state.designShapes, group];
    set({ designShapes: next, selectedDesignShapeIds: [group.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignShape: (id, patch, force = false) => {
    const state = get();
    // Coalesce rapid edits to the same group (slider drags) into one undo step.
    // Discrete/structural edits (Combine toggles, add/remove/duplicate part,
    // primary promotion) pass force=true so they ALWAYS get their own step —
    // otherwise they'd merge into whatever slider/text edit preceded them
    // within the coalescing window and become impossible to revert alone.
    pushDesignUndo(state, force, `update:${id}`);
    const next = state.designShapes.map(g => (g.id === id ? { ...g, ...patch } : g));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignShapes: (ids, patch, force = false) => {
    const state = get();
    if (ids.length === 0) return;
    // Coalesce rapid multi-edits (slider drags on a multi-selection) into one step.
    pushDesignUndo(state, force, `update:${[...ids].sort().join(',')}`);
    const set2 = new Set(ids);
    const next = state.designShapes.map(g => (set2.has(g.id) ? { ...g, ...patch } : g));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignMesh: (groupId, partId, patch, force = false) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    // Coalesce rapid edits to the same part (slider drags / gizmo drags) into one step.
    pushDesignUndo(state, force, `updatepart:${partId}`);
    const next = state.designShapes.map(w => (w.id === groupId
      ? { ...w, parts: (w.parts || []).map(p => (p.id === partId ? { ...p, ...patch } : p)) }
      : w));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  // ── Custom-curve control nodes ─────────────────────────────────
  // The path is seeded (makeDefaultCustomPath) on demand so these always operate
  // on a real array, even for a group that just switched to the Custom curve.
  updateDesignNode: (groupId, index, patch, force = false) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    if (index < 0 || index >= path.length) return;
    // Coalesce rapid gizmo/slider drags on the same node into one undo step.
    pushDesignUndo(state, force, `updatenode:${groupId}:${index}`);
    const nextPath = path.map((p, i) => (i === index ? { ...p, ...patch } : p));
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath } : w));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  addDesignNode: (groupId) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    pushDesignUndo(state, true);
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    // Extend past the tip along the last segment's direction, so the new node
    // appears just beyond the end of the path (not on top of it).
    const last = path[path.length - 1];
    const prev = path[path.length - 2] || last;
    const dx = last.x - prev.x, dy = last.y - prev.y, dz = last.z - prev.z;
    const nn: DesignPathNode = {
      x: Math.round((last.x + (dx || 1)) * 100) / 100,
      y: Math.round((last.y + dy) * 100) / 100,
      z: Math.round((last.z + dz) * 100) / 100,
    };
    const nextPath = [...path, nn];
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath, curve: 'custom' as const } : w));
    set({ designShapes: next, activeDesignNodeIndex: nextPath.length - 1 });
    saveDesignDraft(state.beatmap?.id, next);
  },
  removeDesignNode: (groupId, index) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    if (path.length <= 2 || index < 0 || index >= path.length) return;   // keep at least 2 nodes
    pushDesignUndo(state, true);
    const nextPath = path.filter((_, i) => i !== index);
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath } : w));
    const sel = state.activeDesignNodeIndex;
    set({
      designShapes: next,
      activeDesignNodeIndex: sel == null ? null : Math.min(sel, nextPath.length - 1),
    });
    saveDesignDraft(state.beatmap?.id, next);
  },
  setActiveDesignNodeIndex: (index) => set({ activeDesignNodeIndex: index }),
  removeDesignShape: (id) => get().removeDesignShapes([id]),
  removeDesignShapes: (ids) => {
    const state = get();
    if (ids.length === 0) return;
    pushDesignUndo(state, true);
    const kill = new Set(ids);
    const next = state.designShapes.filter(g => !kill.has(g.id));
    set({
      designShapes: next,
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => !kill.has(id)),
    });
    saveDesignDraft(state.beatmap?.id, next);
  },
  duplicateDesignShape: (id, name) => {
    const state = get();
    const src = state.designShapes.find(g => g.id === id);
    if (!src) return;
    pushDesignUndo(state, true);
    const copy: DesignShape = {
      ...src,
      id: `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      // Nudge forward in time (not jumped to the playhead) so the duplicate
      // sits visibly next to the original instead of landing on top of it.
      beat: src.beat + 2,
      name: name ?? src.name,
    };
    const next = [...state.designShapes, copy];
    set({ designShapes: next, selectedDesignShapeIds: [copy.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null });
    saveDesignDraft(state.beatmap?.id, next);
  },
  // Entering a wall art (a real id) always lands on the primary shape; going
  // back to the list (null) hides the move-gizmo. Node selection always resets.
  selectDesignShape: (id) => set({ selectedDesignShapeIds: id ? [id] : [], activeDesignMeshId: id ? PRIMARY_MESH_ID : null, activeDesignNodeIndex: null }),
  setSelectedDesignShapeIds: (ids) => {
    const uniq = Array.from(new Set(ids));
    // The move-gizmo only makes sense for a single selected wall art.
    set({ selectedDesignShapeIds: uniq, activeDesignMeshId: uniq.length === 1 ? PRIMARY_MESH_ID : null, activeDesignNodeIndex: null });
  },
  // Switching the active shape clears any selected custom-path node.
  setActiveDesignMeshId: (id) => set({ activeDesignMeshId: id, activeDesignNodeIndex: null }),
  setShapeDesignerOpen: (open) => set({ shapeDesignerOpen: open }),
  copyDesignShape: () => {
    const state = get();
    // Copy the primary (last-selected) group.
    const id = state.selectedDesignShapeIds[state.selectedDesignShapeIds.length - 1];
    const src = state.designShapes.find(g => g.id === id);
    if (src) set({ designClipboard: { ...src } });
  },
  pasteDesignShape: () => {
    const state = get();
    const src = state.designClipboard;
    if (!src) return;
    pushDesignUndo(state, true);
    const copy: DesignShape = {
      ...src,
      id: `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      beat: state.playback.currentBeat,
    };
    const next = [...state.designShapes, copy];
    set({ designShapes: next, selectedDesignShapeIds: [copy.id], activeDesignMeshId: PRIMARY_MESH_ID, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },
  undoDesign: () => {
    const state = get();
    if (state.designUndoStack.length === 0) return;
    const prev = state.designUndoStack[state.designUndoStack.length - 1];
    set({
      designShapes: prev,
      designUndoStack: state.designUndoStack.slice(0, -1),
      designRedoStack: [...state.designRedoStack, state.designShapes],
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => prev.some(g => g.id === id)),
    });
    saveDesignDraft(state.beatmap?.id, prev);
    _designUndoKey = '';   // break coalescing so the next edit starts a fresh step
  },
  redoDesign: () => {
    const state = get();
    if (state.designRedoStack.length === 0) return;
    const nextWalls = state.designRedoStack[state.designRedoStack.length - 1];
    set({
      designShapes: nextWalls,
      designRedoStack: state.designRedoStack.slice(0, -1),
      designUndoStack: [...state.designUndoStack, state.designShapes],
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => nextWalls.some(g => g.id === id)),
    });
    saveDesignDraft(state.beatmap?.id, nextWalls);
    _designUndoKey = '';
  },

  addFlashEvent: (event) => {
    const fe = get().flashEvents;
    if (fe.some(e => Math.abs(e.time_beats - event.time_beats) < 0.01)) return;
    const next = [...fe, event].sort((a, b) => a.time_beats - b.time_beats);
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  removeFlashEvent: (beat) => {
    const state = get();
    const next = state.flashEvents.filter(e => Math.abs(e.time_beats - beat) >= 0.01);
    const sel = state.selectedFlashBeats.filter(b => Math.abs(b - beat) >= 0.01);
    const editing = state.editingFlashBeat != null && Math.abs(state.editingFlashBeat - beat) < 0.01
      ? (sel[0] ?? null) : state.editingFlashBeat;
    set({ flashEvents: next, selectedFlashBeats: sel, editingFlashBeat: editing });
    saveFlashDraft(state.beatmap?.id, next);
  },

  updateFlashEvent: (beat, updates) => {
    const next = get().flashEvents.map(e => Math.abs(e.time_beats - beat) < 0.01 ? { ...e, ...updates } : e);
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  moveFlashEvent: (oldBeat, newBeat) => {
    const state = get();
    const fe = state.flashEvents;
    const idx = fe.findIndex(e => Math.abs(e.time_beats - oldBeat) < 0.01);
    if (idx < 0) return;
    // Don't land on top of another light.
    if (fe.some((e, i) => i !== idx && Math.abs(e.time_beats - newBeat) < 0.01)) return;
    const next = fe
      .map((e, i) => (i === idx ? { ...e, time_beats: newBeat } : e))
      .sort((a, b) => a.time_beats - b.time_beats);
    const wasEditing = state.editingFlashBeat != null && Math.abs(state.editingFlashBeat - oldBeat) < 0.01;
    // Keep the selection pointing at the moved light.
    const sel = state.selectedFlashBeats.map(b => Math.abs(b - oldBeat) < 0.01 ? newBeat : b);
    set({ flashEvents: next, selectedFlashBeats: sel, ...(wasEditing ? { editingFlashBeat: newBeat } : {}) });
    saveFlashDraft(state.beatmap?.id, next);
  },

  clearFlashEvents: () => {
    set({ flashEvents: [], selectedFlashBeats: [], editingFlashBeat: null });
    saveFlashDraft(get().beatmap?.id, []);
  },

  setEditingFlashBeat: (beat) => set({
    editingFlashBeat: beat,
    selectedFlashBeats: beat == null ? [] : [beat],
  }),

  setSelectedFlashBeats: (beats) => set({
    selectedFlashBeats: beats,
    editingFlashBeat: beats.length ? beats[beats.length - 1] : null,
  }),

  toggleFlashSelection: (beat, additive) => {
    const state = get();
    if (!additive) {
      // Plain click: if this light is already the sole selection, a second click
      // closes the popup. Otherwise select just this one.
      const isSole = state.selectedFlashBeats.length === 1
        && Math.abs(state.selectedFlashBeats[0] - beat) < 0.01;
      set(isSole
        ? { selectedFlashBeats: [], editingFlashBeat: null }
        : { selectedFlashBeats: [beat], editingFlashBeat: beat });
      return;
    }
    const has = state.selectedFlashBeats.some(b => Math.abs(b - beat) < 0.01);
    const sel = has
      ? state.selectedFlashBeats.filter(b => Math.abs(b - beat) >= 0.01)
      : [...state.selectedFlashBeats, beat];
    set({ selectedFlashBeats: sel, editingFlashBeat: has ? (sel[sel.length - 1] ?? null) : beat });
  },

  updateFlashEvents: (beats, updates) => {
    const inSet = (t: number) => beats.some(b => Math.abs(b - t) < 0.01);
    const next = get().flashEvents.map(e => inSet(e.time_beats) ? { ...e, ...updates } : e);
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  setFlashDefaults: (defaults) => {
    set({ flashDefaults: { ...get().flashDefaults, ...defaults } });
  },

  // ── Select All ─────────────────────────────────────────────────

  selectAll: () => {
    const diff = get().getCurrentDifficulty();
    if (!diff) return;
    set({
      selectedNoteIds: diff.notes.map((_: Note, i: number) => i),
      selectedSlideIds: (diff.slides || []).map((_: Slide, i: number) => i),
    });
  },

  setPatternDifficultyVisible: (visible) => set({ patternDifficultyVisible: visible }),
  setFovPanelOpen: (open) => set({ fovPanelOpen: open }),
  setPerfMode: (mode) => {
    set({ perfMode: mode });
    try { localStorage.setItem('synapse_perf_mode', mode); } catch { /* ignore */ }
  },
  setActiveView: (view) => {
    const st = get();
    const isEditor = (v: ActiveView): v is EditorKey => v === 'synth-rider' || v === 'console';
    // Entering an editor that doesn't own the live map: stash the current map in
    // its owner's slot and restore the target editor's own map (VR ⇄ Console are
    // independent). Non-editor views (dashboard/flow) leave the live map alone.
    if (isEditor(view) && st.mapOwner && st.mapOwner !== view) {
      const outSlot: MapSlot = {
        beatmap: st.beatmap,
        lastBeatmapId: st.lastBeatmapId,
        activeDifficulty: st.activeDifficulty,
        bookmarks: st.bookmarks,
        flashEvents: st.flashEvents,
      };
      const inSlot = st.mapSlots[view];
      set({
        activeView: view,
        mapOwner: view,
        mapSlots: { ...st.mapSlots, [st.mapOwner]: outSlot },
        beatmap: inSlot?.beatmap ?? null,
        lastBeatmapId: inSlot?.lastBeatmapId ?? null,
        activeDifficulty: inSlot?.activeDifficulty ?? st.activeDifficulty,
        bookmarks: inSlot?.bookmarks ?? [],
        flashEvents: inSlot?.flashEvents ?? [],
        selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
        playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
      });
      return;
    }
    // First editor entered this session claims ownership of the auto-loaded map.
    if (isEditor(view) && !st.mapOwner) {
      set({ activeView: view, mapOwner: view });
      return;
    }
    set({ activeView: view });
  },

  // ── Helpers ───────────────────────────────────────────────────

  getCurrentDifficulty: () => {
    const { beatmap, activeDifficulty } = get();
    return beatmap?.difficulties[activeDifficulty] || null;
  },

  // Time snapping follows the VISIBLE "Step" control (scrollStep), not the separate
  // snapDivision dropdown. scrollStep already carries the exact step in beats for
  // every family — including triplets (1/3, 1/6…), which a power-of-two
  // snapDivision could never express, so ternary Step used to be rounded away the
  // moment you placed or paused.
  snapBeat: (beat) => {
    const step = get().scrollStep;
    if (!step || step <= 0) return beat;
    return Math.round(beat / step) * step;
  },

  beatToTimeMs: (beat) => {
    const { beatmap } = get();
    const bpm = beatmap?.bpm || 120;
    return (beat / (bpm / 60)) * 1000;
  },
}));

// ── Internal Helpers ────────────────────────────────────────────────────────

/** Clamp X/Y to grid bounds in note-space (store-level safety net) */
function _clampNotePos(state: EditorState, x: number, y: number): [number, number] {
  const SCALE = GRID_SCALE * 10;
  const noteRadius = (state.gameplaySettings.noteSize ?? 0.175) / SCALE;
  const halfW = (state.gridWidth / 2) / SCALE - noteRadius;
  const halfH = (state.gridHeight / 2) / SCALE - noteRadius;
  const centerY = (state.notesGridYOffset ?? 0) / SCALE;
  return [
    Math.max(-halfW, Math.min(halfW, x)),
    Math.max(centerY - halfH, Math.min(centerY + halfH, y)),
  ];
}

// While an interactive drag is in progress we take ONE snapshot at the start
// (via beginDrag) and suppress the per-frame snapshots the move-updates would
// otherwise push — so undo jumps straight back to the pre-drag state.
let _undoSuppressed = false;

function pushUndo(state: EditorState, label: string) {
  if (_undoSuppressed) return;
  if (!state.beatmap) return;
  const entry: HistoryEntry = {
    label,
    difficulties: JSON.parse(JSON.stringify(state.beatmap.difficulties)),
  };
  const stack = [...state.undoStack.slice(-49), entry];
  useEditorStore.setState({ undoStack: stack, redoStack: [] });
}

// Design-wall undo history (separate from note history). Snapshots are just the
// previous `designShapes` array (immutably replaced on every edit). Continuous
// edits to the SAME group within a short window coalesce into one step so a
// slider drag is a single undo — `force` bypasses coalescing (add/remove/paste).
let _designUndoKey = '';
let _designUndoTs = 0;
function pushDesignUndo(state: EditorState, force: boolean, key = '') {
  const now = Date.now();
  if (!force && key && key === _designUndoKey && now - _designUndoTs < 700) {
    _designUndoTs = now;
    return;
  }
  _designUndoKey = force ? '' : key;
  _designUndoTs = now;
  const stack = [...state.designUndoStack.slice(-49), state.designShapes];
  useEditorStore.setState({ designUndoStack: stack, designRedoStack: [] });
}

function setDiffNotes(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
  notes: Note[]
) {
  const state = get();
  const { beatmap, activeDifficulty } = state;
  if (!beatmap) return;
  const diff = beatmap.difficulties[activeDifficulty];
  if (!diff) return;

  // The scene windows notes with a binary search over time_beats, so an array
  // that is out of time order silently drops notes outside the mounted window —
  // they turn invisible AND unclickable (so they can't be deleted either).
  // Callers that only append already sort; the ones that CHANGE a note's time
  // (updateNote / moveSelectedNotes / rail-node time edits) did not. Enforce it
  // at the single choke point instead, so no future caller can regress it.
  let out = notes;
  let indexFix: Partial<EditorState> | null = null;
  for (let i = 1; i < notes.length; i++) {
    if (notes[i].time_ms < notes[i - 1].time_ms) {
      // Selection, hover and rail placement all address notes by ARRAY INDEX,
      // so re-sorting has to carry those indices to the notes' new positions.
      out = [...notes].sort((a, b) => a.time_ms - b.time_ms);
      const newIndexOf = new Map<Note, number>();
      for (let j = 0; j < out.length; j++) newIndexOf.set(out[j], j);
      const remap = (idx: number | null): number | null => {
        if (idx === null || idx < 0 || idx >= notes.length) return idx;
        const next = newIndexOf.get(notes[idx]);
        return next === undefined ? idx : next;
      };
      const rail = state.selectedRailNode;
      indexFix = {
        selectedNoteIds: state.selectedNoteIds
          .map(remap)
          .filter((i): i is number => i !== null),
        selectedRailNode: rail
          ? { ...rail, noteIndex: remap(rail.noteIndex) ?? rail.noteIndex }
          : null,
        railPlacementNoteIndex: remap(state.railPlacementNoteIndex),
        hoveredNoteIndex: remap(state.hoveredNoteIndex),
      };
      break;
    }
  }

  set(() => ({
    ...indexFix,
    beatmap: {
      ...beatmap,
      difficulties: {
        ...beatmap.difficulties,
        [activeDifficulty]: {
          ...diff,
          notes: out,
          note_count: out.length,
          rail_count: out.filter(n => n.has_rail).length,
          duration_beats: out.length > 0 ? Math.max(...out.map(n => n.time_beats)) : 0,
          duration_seconds: out.length > 0 ? Math.max(...out.map(n => n.time_seconds)) : 0,
        },
      },
    },
  }));
}

function setDiffSlides(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
  slides: Slide[]
) {
  const { beatmap, activeDifficulty } = get();
  if (!beatmap) return;
  const diff = beatmap.difficulties[activeDifficulty];
  if (!diff) return;
  set(() => ({
    beatmap: {
      ...beatmap,
      difficulties: {
        ...beatmap.difficulties,
        [activeDifficulty]: { ...diff, slides, slide_count: slides.length },
      },
    },
  }));
}

// ── Persist preferences to localStorage on change ────────────────────────────

useEditorStore.subscribe((state, prevState) => {
  if (
    state.cameraView !== prevState.cameraView ||
    state.playbackSpeed !== prevState.playbackSpeed ||
    state.scrollStep !== prevState.scrollStep ||
    state.snapDivision !== prevState.snapDivision ||
    state.timelineZoom !== prevState.timelineZoom ||
    state.noteJumpSpeed !== prevState.noteJumpSpeed ||
    state.mapLocked !== prevState.mapLocked ||
    state.musicVolume !== prevState.musicVolume ||
    state.sfxVolume !== prevState.sfxVolume ||
    state.activeDifficulty !== prevState.activeDifficulty ||
    state.leftSidebarOpen !== prevState.leftSidebarOpen ||
    state.leftSidebarWidth !== prevState.leftSidebarWidth ||
    state.rightSidebarOpen !== prevState.rightSidebarOpen ||
    state.rightSidebarWidth !== prevState.rightSidebarWidth ||
    state.bottomPanelOpen !== prevState.bottomPanelOpen ||
    state.bottomPanelHeight !== prevState.bottomPanelHeight ||
    state.showHumanFigure !== prevState.showHumanFigure ||
    state.figureMode !== prevState.figureMode ||
    state.renderMode !== prevState.renderMode ||
    state.heatmapEnabled !== prevState.heatmapEnabled ||
    state.patternDifficultyVisible !== prevState.patternDifficultyVisible ||
    state.fovPanelOpen !== prevState.fovPanelOpen ||
    state.activeView !== prevState.activeView ||
    state.customCameraPosition !== prevState.customCameraPosition ||
    state.customCameraRotation !== prevState.customCameraRotation ||
    state.defaultCameraPosition !== prevState.defaultCameraPosition ||
    state.defaultCameraRotation !== prevState.defaultCameraRotation ||
    state.leftCameraPosition !== prevState.leftCameraPosition ||
    state.leftCameraRotation !== prevState.leftCameraRotation ||
    state.rightCameraPosition !== prevState.rightCameraPosition ||
    state.rightCameraRotation !== prevState.rightCameraRotation ||
    state.mannequinSettings !== prevState.mannequinSettings ||
    state.gameplaySettings !== prevState.gameplaySettings ||
    state.interfaceSettings !== prevState.interfaceSettings ||
    state.particleSettings !== prevState.particleSettings ||
    state.performanceSettings !== prevState.performanceSettings ||
    state.lastBeatmapId !== prevState.lastBeatmapId ||
    state.lastBeatmapIdByEditor !== prevState.lastBeatmapIdByEditor ||
    state.gridVisible !== prevState.gridVisible ||
    state.gridSize !== prevState.gridSize ||
    state.snapToGrid !== prevState.snapToGrid ||
    state.gridGuideType !== prevState.gridGuideType ||
    state.gridGuideVisible !== prevState.gridGuideVisible ||
    state.gridWidth !== prevState.gridWidth ||
    state.gridHeight !== prevState.gridHeight ||
    state.gridDivisionsH !== prevState.gridDivisionsH ||
    state.gridDivisionsV !== prevState.gridDivisionsV ||
    state.gridColor !== prevState.gridColor ||
    state.gridOpacity !== prevState.gridOpacity ||
    state.notesGridYOffset !== prevState.notesGridYOffset ||
    state.minimapCam !== prevState.minimapCam ||
    state.minimapZoom !== prevState.minimapZoom ||
    // Save the scrub position at most ~once per second so a refresh returns to
    // roughly where you were editing.
    Math.floor(state.playback.currentTime) !== Math.floor(prevState.playback.currentTime)
  ) {
    savePrefs({
      cameraView: state.cameraView,
      minimapCam: state.minimapCam,
      minimapZoom: state.minimapZoom,
      lastTime: state.playback.currentTime,
      playbackSpeed: state.playbackSpeed,
      scrollStep: state.scrollStep,
      snapDivision: state.snapDivision,
      timelineZoom: state.timelineZoom,
      noteJumpSpeed: state.noteJumpSpeed,
      mapLocked: state.mapLocked,
      musicVolume: state.musicVolume,
      sfxVolume: state.sfxVolume,
      activeDifficulty: state.activeDifficulty,
      leftSidebarOpen: state.leftSidebarOpen,
      leftSidebarWidth: state.leftSidebarWidth,
      rightSidebarOpen: state.rightSidebarOpen,
      rightSidebarWidth: state.rightSidebarWidth,
      bottomPanelOpen: state.bottomPanelOpen,
      bottomPanelHeight: state.bottomPanelHeight,
      showHumanFigure: state.showHumanFigure,
      figureMode: state.figureMode,
      renderMode: state.renderMode,
      heatmapEnabled: state.heatmapEnabled,
      patternDifficultyVisible: state.patternDifficultyVisible,
      fovPanelOpen: state.fovPanelOpen,
      activeView: state.activeView,
      customCameraPosition: state.customCameraPosition ?? undefined,
      customCameraRotation: state.customCameraRotation ?? undefined,
      defaultCameraPosition: state.defaultCameraPosition ?? undefined,
      defaultCameraRotation: state.defaultCameraRotation ?? undefined,
      leftCameraPosition: state.leftCameraPosition ?? undefined,
      leftCameraRotation: state.leftCameraRotation ?? undefined,
      rightCameraPosition: state.rightCameraPosition ?? undefined,
      rightCameraRotation: state.rightCameraRotation ?? undefined,
      mannequinSettings: state.mannequinSettings,
      gameplaySettings: state.gameplaySettings,
      interfaceSettings: state.interfaceSettings,
      particleSettings: state.particleSettings,
      performanceSettings: state.performanceSettings,
      lastBeatmapId: state.lastBeatmapId ?? undefined,
      lastBeatmapIdByEditor: state.lastBeatmapIdByEditor,
      gridVisible: state.gridVisible,
      gridSize: state.gridSize,
      snapToGrid: state.snapToGrid,
      gridGuideType: state.gridGuideType,
      gridGuideVisible: state.gridGuideVisible,
      gridWidth: state.gridWidth,
      gridHeight: state.gridHeight,
      gridDivisionsH: state.gridDivisionsH,
      gridDivisionsV: state.gridDivisionsV,
      gridColor: state.gridColor,
      gridOpacity: state.gridOpacity,
      notesGridYOffset: state.notesGridYOffset,
    });
  }
});

// ── Autosave edited notes/difficulties to a per-map draft ─────────────────────
// Note edits are otherwise in-memory only, so a page refresh (which reloads the
// backend original) would discard them. Debounced so a burst of edits doesn't
// thrash localStorage. Only fires on a real edit to the *same* map (a load or
// map-switch changes the beatmap id — we flush the previous map's pending draft
// then, but never treat the freshly-loaded difficulties as a new edit).
let _diffDraftTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingDiffDraft: { id: string; difficulties: Record<string, Difficulty> } | null = null;
function flushDiffDraft() {
  if (_diffDraftTimer) { clearTimeout(_diffDraftTimer); _diffDraftTimer = null; }
  if (_pendingDiffDraft) {
    saveDifficultyDraft(_pendingDiffDraft.id, _pendingDiffDraft.difficulties);
    _pendingDiffDraft = null;
  }
}
useEditorStore.subscribe((state, prevState) => {
  const bm = state.beatmap;
  const prevBm = prevState.beatmap;
  // Map load / switch / close: commit any pending edits for the previous map,
  // and don't record the just-loaded difficulties as an edit.
  if (bm?.id !== prevBm?.id) { flushDiffDraft(); return; }
  if (!bm || bm.difficulties === prevBm?.difficulties) return;
  _pendingDiffDraft = { id: bm.id, difficulties: bm.difficulties };
  if (_diffDraftTimer) clearTimeout(_diffDraftTimer);
  _diffDraftTimer = setTimeout(flushDiffDraft, 500);
});
// Flush synchronously on refresh / tab close so the latest edits aren't lost to
// the 500ms debounce (localStorage writes are synchronous).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushDiffDraft);
  window.addEventListener('pagehide', flushDiffDraft);
}

// Console debug handle, same family as __flowStore/__synapseScene/__synapseBudget: lets a bug
// report be reproduced against the real store from devtools, and lets the end-to-end harness set
// up and assert editor state without driving twenty clicks first.
if (typeof window !== 'undefined') {
  (window as unknown as { __editorStore?: unknown }).__editorStore = useEditorStore;
}
